import { FC, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, LineChart, Line, LabelList,
} from 'recharts';
import {
  TrendingUp, ShoppingCart, Percent, XCircle,
  Download, RefreshCw, Star, Gem, Flame,
  TrendingDown, CircleDot, Layers, Pin,
  Package, Filter, Store,
} from 'lucide-react';
import ContextStore from '../hooks/ContextStore';
import { dispatch } from '../dispatch/dispatch';
import { actions } from '../dispatch';
import { IProductResponse } from '../interfaces/responses/ProductResponse.interface';
import { IStoreResponse } from '../interfaces/responses/StoreResponse.interface';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { KpiCard } from '../components/smart-analytics/KpiCard';
import { DateRangeFilter } from '../components/smart-analytics/DateRangeFilter';
import { TokenAlert } from '../components/smart-analytics/TokenAlert';
import { EmptyState } from '../components/smart-analytics/EmptyState';
import { SectionBanner } from '../components/smart-analytics/SectionBanner';
import PriceWidget from '../components/smart-analytics/PriceWidget';
import './SmartAnalytics.page.css';

// ─── Types ───────────────────────────────────────────────────────────────────
type ActionPriority = 1 | 2 | 3;
type ActionType = 'stop-loss' | 'dead-star' | 'fix-returns' | 'almost-star' | 'push-gem' | 'price-up' | 'nurture';

interface ActionItem {
  id: string;
  num: number;
  priority: ActionPriority;
  type: ActionType;
  icon: string;
  title: string;
  detail: string;
  impact: number;
  effort: string;
  product: IProductResponse;
  actionTab: 'stars' | 'gems' | 'rising' | 'bleeding' | 'thin';
}

interface TrackedSku {
  productId: string;
  reason: string;
  trackedAt: string;
}

const TRACKING_PRESETS = ['Price lowered', 'Profit +', 'Sponsor activated', 'Testing price', 'New listing'] as const;

// ─── Helpers ────────────────────────────────────────────────────────────────
const fmt = (d: Date) => d.toISOString().slice(0, 10);

const fmtMAD = (n: number) =>
  `${Math.round(n).toLocaleString('en-US')} MAD`;

const fmtPct = (n: number) => `${n.toFixed(1)}%`;

const totalOrders = (p: IProductResponse) =>
  (p.deliveredOrders || 0) +
  (p.canceledOrders || 0) +
  (p.failedOrders || 0) +
  (p.returnedOrders || 0) +
  (p.pendingOrders || 0) +
  (p.shippedOrders || 0) +
  (p.readyToShipOrders || 0);

const roiBadge = (roi: number) => {
  if (roi >= 50) return { cls: 'sa__roi-badge--green', label: `ROI ${roi.toFixed(0)}%` };
  if (roi >= 20) return { cls: 'sa__roi-badge--amber', label: `ROI ${roi.toFixed(0)}%` };
  return { cls: 'sa__roi-badge--red', label: `ROI ${roi.toFixed(0)}%` };
};

const truncate = (s: string, n: number) =>
  s && s.length > n ? s.slice(0, n) + '…' : (s || '');



// ─── Health status ────────────────────────────────────────────────────────────
type HealthStatus = 'growing' | 'stable' | 'declining' | 'alert';
type UrgencyLevel  = 'urgent' | 'warning' | 'watch' | 'none';

const getHealthStatus = (p: IProductResponse): HealthStatus => {
  const before = p.progressDeliveredOrdersBefore || 0;
  const after  = p.progressDeliveredOrdersAfter  || 0;
  const pct    = p.progressDeliveredOrders        || 0;
  if (after === 0 && before > 0) return 'alert';
  if (pct >  15) return 'growing';
  if (pct < -15) return 'declining';
  return 'stable';
};

// Active orders = delivered + shipped + RTS + pending (excludes canceled/returned/failed)
const activeOrders = (d: any): number =>
  (d.deliveredOrders || 0) + (d.shippedOrders || 0) + (d.readyToShipOrders || 0) + (d.pendingOrders || 0);

// How many consecutive zero-activity days at the END of the chart (most recent)
// A day is "zero" only if it has no active orders at all (nothing in pipeline)
const getZeroTail = (chart: any[]): number => {
  if (!chart || chart.length === 0) return 0;
  let count = 0;
  for (let i = chart.length - 1; i >= 0; i--) {
    if (activeOrders(chart[i]) === 0) count++;
    else break;
  }
  return count;
};

// Is the recent 5-day new-order intake lower than the previous 5-day average?
const getRecentTrend = (chart: any[]): 'up' | 'down' | 'flat' => {
  if (!chart || chart.length < 10) return 'flat';
  const recent = chart.slice(-5);
  const prior  = chart.slice(-10, -5);
  const avg = (arr: any[]) => arr.reduce((s, d) => s + activeOrders(d), 0) / arr.length;
  const rAvg = avg(recent);
  const pAvg = avg(prior);
  if (pAvg === 0) return rAvg > 0 ? 'up' : 'flat';
  const chg = (rAvg - pAvg) / pAvg;
  if (chg >  0.15) return 'up';
  if (chg < -0.15) return 'down';
  return 'flat';
};

const getUrgency = (chart: any[] | undefined): { level: UrgencyLevel; zeroTail: number; recentTrend: 'up' | 'down' | 'flat' } => {
  if (!chart) return { level: 'none', zeroTail: 0, recentTrend: 'flat' };
  const zeroTail    = getZeroTail(chart);
  const recentTrend = getRecentTrend(chart);
  let level: UrgencyLevel = 'none';
  if      (zeroTail >= 3)    level = 'urgent';
  else if (zeroTail === 2)   level = 'warning';
  else if (zeroTail === 1 || recentTrend === 'down') level = 'watch';
  return { level, zeroTail, recentTrend };
};

const healthConfig: Record<HealthStatus, { label: string; color: string; bg: string; border: string }> = {
  growing:   { label: '↑ Growing',   color: '#1D9E75', bg: '#F0FDF4', border: '#A7F3D0' },
  stable:    { label: '→ Stable',    color: '#378ADD', bg: '#EFF6FF', border: '#BFDBFE' },
  declining: { label: '↓ Declining', color: '#EF9F27', bg: '#FFFBEB', border: '#FDE68A' },
  alert:     { label: '⚠ Alert',     color: '#E24B4A', bg: '#FEF2F2', border: '#FECACA' },
};

const urgencyConfig = {
  urgent:  { label: '🔴 URGENT',   color: '#E24B4A', bg: '#FEF2F2', border: '#E24B4A' },
  warning: { label: '🟠 WARNING',  color: '#C2410C', bg: '#FFF7ED', border: '#FDBA74' },
  watch:   { label: '🟡 Watch',    color: '#B45309', bg: '#FFFBEB', border: '#FCD34D' },
  none:    { label: '',            color: '',         bg: '',         border: ''        },
};

const trendConfig = {
  up:   { label: '↑ Increasing', color: '#1D9E75', bg: '#F0FDF4', border: '#A7F3D0' },
  down: { label: '↓ Declining',  color: '#C2410C', bg: '#FFF7ED', border: '#FDBA74' },
  flat: { label: '',             color: '',         bg: '',         border: ''        },
};

const getRecommendations = (
  p: IProductResponse,
  health: HealthStatus,
  urgency: UrgencyLevel,
  zeroTail: number,
  recentTrend: 'up' | 'down' | 'flat'
): string[] => {
  const recs: string[] = [];
  const hasRoomToCut = (p.roi || 0) > 35;
  const roi = (p.roi || 0).toFixed(0);

  // ── Urgent (3+ zero days): drop everything, maximum urgency ─────────────
  if (urgency === 'urgent') {
    recs.push(`No orders in pipeline for ${zeroTail} days in a row — act NOW before a competitor permanently takes this slot`);
    recs.push('Open Jumia NOW and search this exact product — check who appears above you and their price');
    recs.push('Drop price by 10–20 MAD immediately to recapture buyers, even temporarily — visibility first');
    if (hasRoomToCut) recs.push(`ROI ${roi}% gives you room to cut and still profit — use it to fight back`);
    recs.push('Check if your listing is still active — search by SKU on Jumia seller center to confirm');
    recs.push('Check your stock level — if out of stock on Jumia side, restock immediately');
    recs.push('Activate Jumia Express if not active — it restores search placement and shows a fast-shipping badge');
    return recs;
  }

  // ── Warning (2 zero days): something is definitely wrong, act now ────────
  if (urgency === 'warning') {
    recs.push('2 consecutive days without orders — this is unusual for a Star SKU, something has changed');
    recs.push('Search your product on Jumia right now and identify who is ranked above you — it is probably a new competitor or a price undercut');
    recs.push('Compare your price with the top 3 listings — even a 10 MAD difference can shift all buyers away from you');
    if (hasRoomToCut) recs.push(`Your ROI is ${roi}% — you have room to lower the price and still make good profit`);
    recs.push('Check if your SKU is still listed and not suspended — verify in Jumia seller center');
    recs.push('Do not wait for a 3rd zero day — act now while the SKU is still recoverable');
    return recs;
  }

  // ── Watch (1 zero day or recent decline): early warning, stay alert ──────
  if (urgency === 'watch') {
    if (zeroTail === 1) {
      recs.push('No orders today — this could be a one-off, but for a Star SKU it is worth watching');
      recs.push('If tomorrow is also zero, investigate immediately: price, competitors, listing status');
    }
    if (recentTrend === 'down') {
      recs.push('Orders have been declining over the last 5 days — something shifted, investigate now before it gets worse');
      recs.push('Search your product on Jumia and check if a new competitor entered with a lower price');
    }
    recs.push('Compare your price with similar listings — a small adjustment can recover volume quickly');
  }

  // ── Period-level recommendations ──────────────────────────────────────────
  if (health === 'alert') {
    recs.push('Orders went to zero in the second half — a competitor almost certainly moved in');
    recs.push('Check your listing visibility — it may have been buried or a new seller undercut your price');
    if (hasRoomToCut) recs.push(`ROI ${roi}% gives you room to cut price and still profit well`);
  }

  if (health === 'declining') {
    recs.push('Orders were higher earlier in the period — something changed, likely a competitor or price issue');
    recs.push('Search your product on Jumia — check who is ranked just above you today vs 2 weeks ago');
    if (hasRoomToCut) recs.push('A 5–15 MAD price drop can reclaim lost volume — your margin can absorb it');
    recs.push('Jumia Express restores visibility: faster shipping + placement badge = more clicks');
  }

  if (health === 'stable') {
    recs.push('Consistent orders — but flat means someone could enter the category and take share unnoticed');
    recs.push('Set a rule: if 2 consecutive days with no orders, investigate immediately');
    recs.push('A small price drop could break out of flat and grow volume before competitors notice');
    recs.push('Jumia Express gives you a visibility edge — act before a competitor claims it first');
  }

  if (health === 'growing') {
    recs.push('Keep stock high — running out on a growing product is the most expensive mistake you can make');
    if ((p.roi || 0) > 50) recs.push(`ROI ${roi}% is very high — lower the price slightly to dominate the SKU and block new competitors`);
    recs.push('Jumia Express can accelerate this further: faster delivery converts more browsers into buyers');
    recs.push('Do not raise price while growing — protect the momentum, volume is more valuable right now');
  }

  return recs;
};

// ─── Custom chart tooltip ────────────────────────────────────────────────────
const ChartTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="sa__tooltip">
      <div style={{ marginBottom: '0.15rem', color: '#9CA3AF' }}>{label}</div>
      <div>{fmtMAD(payload[0].value)}</div>
    </div>
  );
};

// ─── Component ───────────────────────────────────────────────────────────────
const SmartAnalyticsPage: FC = () => {
  const { contextStore } = useContext(ContextStore);
  const navigate = useNavigate();

  const today = new Date();
  const defaultTo = new Date(today); defaultTo.setDate(today.getDate() - 7);
  const defaultFrom = new Date(today); defaultFrom.setDate(today.getDate() - 14);

  // State
  const [products, setProducts] = useState<IProductResponse[]>([]);
  const [stores, setStores] = useState<IStoreResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState(fmt(defaultFrom));
  const [toDate, setToDate] = useState(fmt(defaultTo));
  const [selectedStore, setSelectedStore] = useState('0');
  const [selectedPG, setSelectedPG] = useState('');
  const [activeTab, setActiveTab] = useState<'plan' | 'stars' | 'gems' | 'rising' | 'bleeding' | 'thin' | 'watched'>('plan');
  const [starsView, setStarsView] = useState<'simulator' | 'health'>('health');
  const [priceAdj, setPriceAdj] = useState(0);
  const [volAdj, setVolAdj] = useState(0);
  const [dailyData, setDailyData] = useState<Record<string, any[]>>({});
  const [loadingDaily, setLoadingDaily] = useState<Record<string, boolean>>({});
  const requestedDaily = useRef<Set<string>>(new Set());
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  // Increment to trigger a fetch — only the Apply button or store-change should increment this
  const [fetchTrigger, setFetchTrigger] = useState(0);
  // Always points to the latest fetchProducts so the effect closure is never stale
  const fetchProductsRef = useRef<() => Promise<void>>(() => Promise.resolve());
  const [actionLogs, setActionLogs] = useState<any[]>([]);
  const [savingAction, setSavingAction] = useState<string | null>(null);
  const [trackedSkus, setTrackedSkus] = useState<TrackedSku[]>([]);
  const [addTrackingFor, setAddTrackingFor] = useState<string | null>(null);
  const [imgPopup, setImgPopup] = useState<{ uri: string; top: number; left: number } | null>(null);
  const handleNameClick = (e: React.MouseEvent, uri: string) => {
    if (!uri) return;
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setImgPopup({ uri, top: r.bottom + 8, left: r.left });
  };
  const [trackingReason, setTrackingReason] = useState('');
  const [editingReasonFor, setEditingReasonFor] = useState<string | null>(null);
  const [editReasonValue, setEditReasonValue] = useState('');
  const [tokenHealth, setTokenHealth] = useState<{ healthy: boolean; expiredStores: { name: string }[]; zeroPriceCount: number } | null>(null);
  const [refreshingTokens, setRefreshingTokens] = useState(false);

  // Fetch products
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const body = {
        fromDate: new Date(fromDate).toISOString(),
        toDate: new Date(toDate).toISOString(),
      };
      let resp: IProductResponse[] | any;
      if (selectedStore === '0') {
        resp = await dispatch(actions.getAllProductGroupProducts, body, contextStore.token);
      } else {
        resp = await dispatch(
          actions.getStoreProducts, body, contextStore.token, { storeId: selectedStore }
        );
      }
      if (resp && !('error' in resp)) {
        setProducts(Array.isArray(resp) ? resp : []);
        setLastUpdated(new Date());
      }
    } catch {
      setProducts([]);
    }
    setLoading(false);
  }, [fromDate, toDate, selectedStore, contextStore.token]);

  // Fetch daily chart for a product — uses a ref to prevent duplicate requests
  const loadDailyChart = useCallback(async (productId: string) => {
    if (requestedDaily.current.has(productId)) return;
    requestedDaily.current.add(productId);
    setLoadingDaily(prev => ({ ...prev, [productId]: true }));
    try {
      const resp = await dispatch(
        actions.getAnalyticsProduct,
        {
          fromDate: new Date(fromDate).toISOString(),
          toDate:   new Date(toDate).toISOString(),
          productId,
          type: 'order',
        },
        contextStore.token
      );
      if (resp && !('error' in resp) && Array.isArray(resp)) {
        setDailyData(prev => ({ ...prev, [productId]: resp }));
      }
    } catch {}
    setLoadingDaily(prev => ({ ...prev, [productId]: false }));
  }, [fromDate, toDate, contextStore.token]);

  // Reset daily data when date range or store changes
  useEffect(() => {
    setDailyData({});
    requestedDaily.current.clear();
  }, [fromDate, toDate, selectedStore]);

  // Fetch stores list once
  useEffect(() => {
    (async () => {
      const resp = await dispatch(actions.getStores, {}, contextStore.token);
      if (resp && !('error' in resp)) setStores(resp);
    })();
  }, [contextStore.token]);

  // Fetch action logs
  const fetchActionLogs = useCallback(async () => {
    const resp = await dispatch(actions.getActionLogs, {}, contextStore.token);
    if (Array.isArray(resp)) setActionLogs(resp);
  }, [contextStore.token]);

  useEffect(() => { fetchActionLogs(); }, [fetchActionLogs]);

  const markActionDone = useCallback(async (item: ActionItem) => {
    setSavingAction(item.id);
    await dispatch(actions.saveActionLog, {
      productId: item.product._id,
      actionType: item.type,
      actionTitle: item.title,
      baseline: {
        deliveredOrders:              item.product.deliveredOrders              || 0,
        roi:                          item.product.roi                          || 0,
        profitPerProduct:             item.product.profitPerProduct             || 0,
        totalProfit:                  item.product.totalProfit                  || 0,
        progressDeliveredOrdersAfter: item.product.progressDeliveredOrdersAfter || 0,
        canceledOrders:               item.product.canceledOrders               || 0,
        returnedOrders:               item.product.returnedOrders               || 0,
      },
    }, contextStore.token);
    await fetchActionLogs();
    setSavingAction(null);
  }, [contextStore.token, fetchActionLogs]);

  const deleteActionLog = useCallback(async (logId: string) => {
    await dispatch(actions.deleteActionLog, {}, contextStore.token, { id: logId });
    await fetchActionLogs();
  }, [contextStore.token, fetchActionLogs]);

  // Token health — poll every 5 minutes
  const checkTokenHealth = useCallback(async () => {
    const resp = await dispatch(actions.getTokenHealth, {}, contextStore.token);
    if (resp && !('error' in resp)) setTokenHealth(resp);
  }, [contextStore.token]);

  useEffect(() => {
    checkTokenHealth();
    const interval = setInterval(checkTokenHealth, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [checkTokenHealth]);

  const handleRefreshTokens = useCallback(async () => {
    setRefreshingTokens(true);
    await dispatch(actions.refreshTokens, {}, contextStore.token);
    // Poll health again after 3 minutes (refresh job takes ~3min for 11 stores)
    setTimeout(() => {
      checkTokenHealth();
      setRefreshingTokens(false);
    }, 3 * 60 * 1000);
  }, [contextStore.token, checkTokenHealth]);


  // Load tracked SKUs from DB on mount
  useEffect(() => {
    (async () => {
      const resp = await dispatch(actions.getTrackedSkus, {}, contextStore.token);
      if (Array.isArray(resp)) setTrackedSkus(resp);
    })();
  }, [contextStore.token]);

  const isTracked = useCallback((productId: string) =>
    trackedSkus.some(t => t.productId === productId),
  [trackedSkus]);

  const addToTracking = useCallback(async (productId: string, reason: string) => {
    // Optimistic update
    setTrackedSkus(prev =>
      prev.find(t => t.productId === productId)
        ? prev
        : [...prev, { productId, reason: reason.trim() || 'Tracking', trackedAt: new Date().toISOString() }]
    );
    setAddTrackingFor(null);
    setTrackingReason('');
    const resp = await dispatch(actions.addTrackedSku, { productId, reason: reason.trim() || 'Tracking' }, contextStore.token);
    if (Array.isArray(resp)) setTrackedSkus(resp);
  }, [contextStore.token]);

  const removeFromTracking = useCallback(async (productId: string) => {
    setTrackedSkus(prev => prev.filter(t => t.productId !== productId));
    const resp = await dispatch(actions.removeTrackedSku, {}, contextStore.token, { productId });
    if (Array.isArray(resp)) setTrackedSkus(resp);
  }, [contextStore.token]);

  const updateTrackingReason = useCallback(async (productId: string, reason: string) => {
    setTrackedSkus(prev =>
      prev.map(t => t.productId === productId ? { ...t, reason } : t)
    );
    const resp = await dispatch(actions.updateTrackedSkuReason, { reason }, contextStore.token, { productId });
    if (Array.isArray(resp)) setTrackedSkus(resp);
  }, [contextStore.token]);

  // Keep the ref pointing at the latest version of fetchProducts
  useEffect(() => { fetchProductsRef.current = fetchProducts; });

  // Only fetch when the trigger changes (Apply button) — date input changes alone don't fetch
  useEffect(() => { fetchProductsRef.current(); }, [fetchTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-fetch when the store filter changes (not a date change)
  const prevStoreRef = useRef(selectedStore);
  useEffect(() => {
    if (prevStoreRef.current !== selectedStore) {
      prevStoreRef.current = selectedStore;
      fetchProductsRef.current();
    }
  }, [selectedStore]); // eslint-disable-line react-hooks/exhaustive-deps


  // Product group filter (client-side)
  const filtered = useMemo(() => {
    if (!selectedPG) return products;
    return products.filter(p => p.productGroup?._id === selectedPG);
  }, [products, selectedPG]);

  // Unique product groups extracted from fetched products
  const productGroups = useMemo(() => {
    const map = new Map<string, string>();
    products.forEach(p => {
      if (p.productGroup?._id) map.set(p.productGroup._id, p.productGroup.name);
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [products]);

  // ── KPIs ──────────────────────────────────────────────────────────────────
  const kpi = useMemo(() => {
    const totalProfit = filtered.reduce((s, p) => s + (p.totalProfit || 0), 0);
    const tOrders    = filtered.reduce((s, p) => s + totalOrders(p), 0);
    const delivered  = filtered.reduce((s, p) => s + (p.deliveredOrders || 0), 0);
    const failed     = filtered.reduce((s, p) => s + (p.failedOrders || 0), 0);
    return {
      totalProfit,
      tOrders,
      delivered,
      failed,
      deliveryRate: tOrders > 0 ? (delivered / tOrders) * 100 : 0,
      failedRate:   tOrders > 0 ? (failed   / tOrders) * 100 : 0,
    };
  }, [filtered]);

  // ── Funnel ────────────────────────────────────────────────────────────────
  const funnelData = useMemo(() => {
    const delivered = filtered.reduce((s, p) => s + (p.deliveredOrders || 0), 0);
    const failed    = filtered.reduce((s, p) => s + (p.failedOrders    || 0), 0);
    const returned  = filtered.reduce((s, p) => s + (p.returnedOrders  || 0), 0);
    const canceled  = filtered.reduce((s, p) => s + (p.canceledOrders  || 0), 0);
    const total     = kpi.tOrders;
    const pct = (n: number) => (total > 0 ? (n / total) * 100 : 0);
    return [
      { label: 'Total',     count: total,     pct: 100,          color: '#378ADD' },
      { label: 'Delivered', count: delivered,  pct: pct(delivered), color: '#1D9E75' },
      { label: 'Failed',    count: failed,     pct: pct(failed),    color: '#E24B4A' },
      { label: 'Returned',  count: returned,   pct: pct(returned),  color: '#EF9F27' },
      { label: 'Canceled',  count: canceled,   pct: pct(canceled),  color: '#9CA3AF' },
    ];
  }, [filtered, kpi.tOrders]);

  // ── Top 8 products by profit ──────────────────────────────────────────────
  const top8 = useMemo(() =>
    [...filtered]
      .filter(p => (p.totalProfit || 0) > 0)
      .sort((a, b) => (b.totalProfit || 0) - (a.totalProfit || 0))
      .slice(0, 8)
      .map(p => ({ ...p, shortName: truncate(p.name, 24) }))
  , [filtered]);

  // ── Top product groups by profit ──────────────────────────────────────────
  const topPG = useMemo(() => {
    const map = new Map<string, { name: string; profit: number }>();
    filtered.forEach(p => {
      const key  = p.productGroup?._id  || '__none__';
      const name = p.productGroup?.name || 'Ungrouped';
      const prev = map.get(key) || { name, profit: 0 };
      map.set(key, { name, profit: prev.profit + (p.totalProfit || 0) });
    });
    return Array.from(map.values())
      .filter(g => g.profit > 0)
      .sort((a, b) => b.profit - a.profit)
      .slice(0, 8);
  }, [filtered]);

  const maxPGProfit = topPG[0]?.profit || 1;

  // ── Stores by profit ──────────────────────────────────────────────────────
  const topStores = useMemo(() => {
    const map = new Map<string, { name: string; profit: number; delivered: number }>();
    filtered.forEach(p => {
      const key  = p.store?._id  || '__none__';
      const name = p.store?.name || 'Unknown';
      const prev = map.get(key) || { name, profit: 0, delivered: 0 };
      map.set(key, {
        name,
        profit:    prev.profit    + (p.totalProfit    || 0),
        delivered: prev.delivered + (p.deliveredOrders || 0),
      });
    });
    return Array.from(map.values())
      .sort((a, b) => b.profit - a.profit);
  }, [filtered]);

  const maxStoreProfit = topStores[0]?.profit || 1;

  // ── Segments ──────────────────────────────────────────────────────────────
  const stars = useMemo(() =>
    filtered.filter(p => (p.roi || 0) >= 25 && (p.deliveredOrders || 0) >= 10)
  , [filtered]);

  // Auto-fetch daily data for all Stars when health monitor view is open
  useEffect(() => {
    if (starsView === 'health' && stars.length > 0) {
      stars.forEach(p => loadDailyChart(p._id));
    }
  }, [starsView, stars, loadDailyChart]);

  const gems = useMemo(() =>
    filtered
      .filter(p =>
        (p.roi || 0) >= 50 &&
        (p.deliveredOrders || 0) < 15 &&
        (p.profitPerProduct || 0) > 0
      )
      .sort((a, b) => (b.roi || 0) * (b.deliveredOrders || 0) - (a.roi || 0) * (a.deliveredOrders || 0))
  , [filtered]);

  // Auto-fetch daily data for gems when gems tab is active
  useEffect(() => {
    if (activeTab === 'gems' && gems.length > 0) {
      gems.forEach(p => loadDailyChart(p._id));
    }
  }, [activeTab, gems, loadDailyChart]);

  const thin = useMemo(() =>
    filtered.filter(p =>
      (p.deliveredOrders || 0) >= 5 &&
      (p.profitPerProduct || 0) > 0 &&
      (p.profitPerProduct || 0) < 60
    )
  , [filtered]);

  // Rising Stars: profitable, not yet a Star, had recent activity (orders in second half of period)
  const rising = useMemo(() =>
    filtered
      .filter(p =>
        (p.profitPerProduct || 0) > 0 &&
        (p.deliveredOrders || 0) >= 1 &&
        !((p.roi || 0) >= 25 && (p.deliveredOrders || 0) >= 10) // not already a Star
      )
      .sort((a, b) => {
        // Primary: most recent activity (orders in second half of period)
        const aRecent = (a.progressDeliveredOrdersAfter || 0);
        const bRecent = (b.progressDeliveredOrdersAfter || 0);
        if (bRecent !== aRecent) return bRecent - aRecent;
        // Secondary: closest to Star threshold (fewer missing orders)
        const aMissing = Math.max(0, 10 - (a.deliveredOrders || 0));
        const bMissing = Math.max(0, 10 - (b.deliveredOrders || 0));
        return aMissing - bMissing;
      })
  , [filtered]);

  // Auto-fetch daily charts for rising stars when tab is active
  useEffect(() => {
    if (activeTab === 'rising' && rising.length > 0) {
      rising.forEach(p => loadDailyChart(p._id));
    }
  }, [activeTab, rising, loadDailyChart]);

  // Bleeding: losing money per unit OR total loss over period (returns destroying profit)
  const bleeding = useMemo(() =>
    filtered
      .filter(p =>
        ((p.deliveredOrders || 0) > 0 || (p.returnedOrders || 0) > 0) &&
        ((p.profitPerProduct || 0) < 0 || (p.totalProfit || 0) < 0)
      )
      .sort((a, b) => (a.totalProfit || 0) - (b.totalProfit || 0)) // worst first
  , [filtered]);

  // Auto-fetch daily charts for bleeding SKUs when tab is active
  useEffect(() => {
    if (activeTab === 'bleeding' && bleeding.length > 0) {
      bleeding.forEach(p => loadDailyChart(p._id));
    }
  }, [activeTab, bleeding, loadDailyChart]);

  // Watched products — resolve tracked IDs against current product list
  const watchedProducts = useMemo(() =>
    trackedSkus
      .map(t => ({ tracking: t, product: products.find(p => p._id === t.productId) }))
      .filter((x): x is { tracking: TrackedSku; product: IProductResponse } => x.product !== undefined),
  [trackedSkus, products]);

  // Auto-fetch daily charts when watched tab is active
  useEffect(() => {
    if (activeTab === 'watched' && watchedProducts.length > 0) {
      watchedProducts.forEach(({ product }) => loadDailyChart(product._id));
    }
  }, [activeTab, watchedProducts, loadDailyChart]);

  // Stars profit simulation
  const starsBase = stars.reduce((s, p) => s + (p.totalProfit || 0), 0);
  const starsProj = useMemo(() =>
    stars.reduce((s, p) => {
      const adj = (p.deliveredOrders || 0) *
        ((p.profitPerProduct || 0) + priceAdj) *
        (1 + volAdj / 100);
      return s + adj;
    }, 0)
  , [stars, priceAdj, volAdj]);

  // Thin margin aggregate impact
  const thinPlus5  = thin.reduce((s, p) => s + (p.deliveredOrders || 0) * 5,  0);
  const thinPlus10 = thin.reduce((s, p) => s + (p.deliveredOrders || 0) * 10, 0);

  // ── Action Plan ───────────────────────────────────────────────────────────
  const daysInPeriod = Math.max(1,
    (new Date(toDate).getTime() - new Date(fromDate).getTime()) / (1000 * 60 * 60 * 24)
  );

  const actionPlan = useMemo((): ActionItem[] => {
    const raw: Omit<ActionItem, 'num'>[] = [];

    // ── P1: Stop-loss — products losing money with active deliveries ──────
    bleeding.forEach(p => {
      const ppp      = p.profitPerProduct || 0;
      const delivered = p.deliveredOrders || 0;
      if (delivered === 0 || ppp >= 0) return;
      const totalLoss  = Math.abs(p.totalProfit || 0);
      const costUnit   = (p.buyingRate || 0) + (p.transportFees || 0) + (p.storageFees || 0) + (p.packageFees || 0);
      const avgPayout  = delivered > 0 ? (p.totalPayout || 0) / delivered : 0;
      const priceGap   = Math.max(0, costUnit - avgPayout);
      raw.push({
        id: `stop-${p._id}`, priority: 1, type: 'stop-loss', icon: '🛑',
        title: `Stop: "${truncate(p.name, 30)}" loses ${fmtMAD(Math.abs(ppp))} per delivery`,
        detail: `${delivered} orders × ${fmtMAD(Math.abs(ppp))} = ${fmtMAD(totalLoss)} lost so far. Raise selling price so Jumia pays at least ${fmtMAD(Math.round(priceGap))} more per unit, or pause the listing immediately.`,
        impact: totalLoss, effort: '5 min', product: p, actionTab: 'bleeding',
      });
    });

    // ── P1: Dead Star — alert health (0 orders in second half) ───────────
    stars.forEach(p => {
      if ((p.progressDeliveredOrdersAfter || 0) > 0) return;
      if ((p.progressDeliveredOrdersBefore || 0) === 0) return;
      const dailyProfit = (p.totalProfit || 0) / daysInPeriod;
      raw.push({
        id: `dead-${p._id}`, priority: 1, type: 'dead-star', icon: '⚠️',
        title: `STAR "${truncate(p.name, 30)}" went silent — investigate NOW`,
        detail: `${p.progressDeliveredOrdersBefore} orders in first half, 0 in second half. A competitor likely undercut your price or your listing went down. Losing ~${fmtMAD(Math.round(dailyProfit * 7))}/week while inactive.`,
        impact: dailyProfit * 14, effort: '10 min', product: p, actionTab: 'stars',
      });
    });

    // ── P1: High return rate causing losses ───────────────────────────────
    filtered.forEach(p => {
      const delivered = p.deliveredOrders || 0;
      const returned  = p.returnedOrders  || 0;
      const total     = delivered + returned;
      const returnRate = total > 0 ? returned / total : 0;
      const loss       = p.loss || 0;
      if (returnRate < 0.4 || returned < 3 || loss < 300) return;
      if (bleeding.some(b => b._id === p._id)) return; // already in stop-loss
      raw.push({
        id: `returns-${p._id}`, priority: 1, type: 'fix-returns', icon: '🔄',
        title: `Fix listing "${truncate(p.name, 30)}" — ${(returnRate * 100).toFixed(0)}% return rate`,
        detail: `${returned} returns out of ${total} orders — lost ${fmtMAD(loss)} on buying cost alone. Inaccurate photos or description is almost always the cause. Fix them today.`,
        impact: loss, effort: '30 min', product: p, actionTab: 'bleeding',
      });
    });

    // ── P2: Declining Stars ───────────────────────────────────────────────
    stars.forEach(p => {
      const health    = getHealthStatus(p);
      const pct       = p.progressDeliveredOrders || 0;
      if (health !== 'declining' || pct > -20) return;
      if ((p.progressDeliveredOrdersAfter || 0) === 0) return; // already P1
      const dailyProfit = (p.totalProfit || 0) / daysInPeriod;
      raw.push({
        id: `decline-${p._id}`, priority: 2, type: 'dead-star', icon: '📉',
        title: `STAR "${truncate(p.name, 30)}" declining ${Math.abs(pct).toFixed(0)}% — check today`,
        detail: `Orders dropped from ${p.progressDeliveredOrdersBefore} to ${p.progressDeliveredOrdersAfter} between first and second half. Search on Jumia now — if a competitor undercut your price, react before it gets worse.`,
        impact: dailyProfit * 7, effort: '10 min', product: p, actionTab: 'stars',
      });
    });

    // ── P2: Almost-Stars — ROI ≥ 25%, 7-9 orders away ───────────────────
    rising.forEach(p => {
      const roi       = p.roi || 0;
      const delivered = p.deliveredOrders || 0;
      const ppp       = p.profitPerProduct || 0;
      if (roi < 25 || delivered < 7 || delivered >= 10) return;
      const ordersLeft = 10 - delivered;
      raw.push({
        id: `almost-${p._id}`, priority: 2, type: 'almost-star', icon: '✨',
        title: `"${truncate(p.name, 30)}" needs ${ordersLeft} more order${ordersLeft > 1 ? 's' : ''} to become a ★ Star`,
        detail: `ROI ${roi.toFixed(0)}% and profit ${fmtMAD(ppp)}/unit — you have room to drop price 10–15 MAD temporarily. Getting to 10 deliveries unlocks the Star tier and improves Jumia ranking.`,
        impact: ppp * ordersLeft + ppp * 10, effort: '10 min', product: p, actionTab: 'rising',
      });
    });

    // ── P2: Top 2 Hidden Gems — scale up the best ones ───────────────────
    gems.slice(0, 2).forEach(p => {
      const roi       = p.roi || 0;
      const ppp       = p.profitPerProduct || 0;
      const delivered = p.deliveredOrders || 0;
      const potentialGain = Math.max(0, ppp * 10 - (p.totalProfit || 0));
      raw.push({
        id: `gem-${p._id}`, priority: 2, type: 'push-gem', icon: '💎',
        title: `Scale "${truncate(p.name, 30)}" — ${roi.toFixed(0)}% ROI, only ${delivered} orders`,
        detail: `Reaching 10 delivered orders adds ~${fmtMAD(potentialGain)} this period. Lower price 10–20 MAD to attract more buyers — your ROI easily absorbs it. Or create a second listing variant.`,
        impact: potentialGain, effort: '15 min', product: p, actionTab: 'gems',
      });
    });

    // ── P3: Rising stars with momentum (4-6 orders) ──────────────────────
    rising
      .filter(p => {
        const d = p.deliveredOrders || 0;
        const r = p.roi || 0;
        return d >= 4 && d < 7 && r >= 20 && !raw.some(a => a.id === `almost-${p._id}`);
      })
      .slice(0, 2)
      .forEach(p => {
        const ppp        = p.profitPerProduct || 0;
        const ordersLeft = 10 - (p.deliveredOrders || 0);
        raw.push({
          id: `nurture-${p._id}`, priority: 3, type: 'nurture', icon: '🌱',
          title: `Nurture "${truncate(p.name, 30)}" — ${ordersLeft} orders from ★`,
          detail: `Good momentum (${p.deliveredOrders} orders, ROI ${(p.roi||0).toFixed(0)}%). Check the listing has accurate photos and keywords. Keep price competitive — don't raise it while building volume.`,
          impact: ppp * ordersLeft, effort: '15 min', product: p, actionTab: 'rising',
        });
      });

    // ── P3: Top thin margin price-up opportunities ────────────────────────
    [...thin]
      .sort((a, b) => (b.deliveredOrders || 0) * 10 - (a.deliveredOrders || 0) * 10)
      .slice(0, 3)
      .forEach(p => {
        const impact = (p.deliveredOrders || 0) * 10;
        raw.push({
          id: `price-${p._id}`, priority: 3, type: 'price-up', icon: '💰',
          title: `Raise price on "${truncate(p.name, 30)}" — +${fmtMAD(impact)} potential`,
          detail: `Profit/unit is ${fmtMAD(p.profitPerProduct || 0)} — thin margin but selling well. A +10 MAD increase adds ${fmtMAD(impact)} if volume holds. Test for 3 days — if orders drop, revert.`,
          impact, effort: '5 min', product: p, actionTab: 'thin',
        });
      });

    // Sort P1→P3, then by impact desc, assign num
    const sorted = [...raw].sort((a, b) =>
      a.priority !== b.priority ? a.priority - b.priority : b.impact - a.impact
    );
    return sorted.map((item, i) => ({ ...item, num: i + 1 }));
  }, [bleeding, stars, gems, rising, thin, filtered, daysInPeriod]);

  // ── CSV Export ────────────────────────────────────────────────────────────
  const handleExport = () => {
    const headers = [
      'SKU', 'Name', 'Store', 'Product Group',
      'Delivered', 'Canceled', 'Failed', 'Returned',
      'Total Profit (MAD)', 'Profit/Product (MAD)', 'Buying Price (MAD)', 'ROI (%)',
    ];
    const rows = filtered.map(p => [
      p.Sku,
      p.name,
      p.store?.name,
      p.productGroup?.name,
      p.deliveredOrders,
      p.canceledOrders,
      p.failedOrders,
      p.returnedOrders,
      (p.totalProfit || 0).toFixed(2),
      (p.profitPerProduct || 0).toFixed(2),
      (p.buyingRate || 0).toFixed(2),
      (p.roi || 0).toFixed(1),
    ]);
    const csv = [headers, ...rows]
      .map(r => r.map(v => `"${v ?? ''}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `smart-analytics-${fromDate}-to-${toDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col min-h-full bg-background">

      {/* ── Page header ─────────────────────────────────────────────────── */}
      <div className="sticky top-0 z-20 border-b border-border bg-background/95 backdrop-blur-sm">
        <div className="flex flex-wrap items-center gap-2 px-4 py-3 lg:px-6">

          {/* Left: title + segment badges */}
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-brand/10">
                <TrendingUp className="h-3.5 w-3.5 text-brand" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground leading-none">Analytics</p>
                <p className="text-sm font-semibold text-foreground leading-tight">
                  {selectedStore === '0' ? 'All Stores' : stores.find(s => s._id === selectedStore)?.name || 'Store'}
                </p>
              </div>
            </div>
            {/* Quick segment counts */}
            <div className="hidden sm:flex items-center gap-1.5">
              <Badge variant="success" className="gap-1 text-[10px]">
                <Star className="h-2.5 w-2.5" />{stars.length}
              </Badge>
              {bleeding.length > 0 && (
                <Badge variant="destructive" className="gap-1 text-[10px]">
                  <Flame className="h-2.5 w-2.5" />{bleeding.length}
                </Badge>
              )}
              <Badge variant="info" className="gap-1 text-[10px]">
                <Gem className="h-2.5 w-2.5" />{gems.length}
              </Badge>
            </div>
          </div>

          {/* Right: filters */}
          <div className="flex flex-wrap items-center gap-2">

            {/* Store filter — drives fetch */}
            {stores.length > 0 && (
              <div className="flex items-center gap-1.5">
                <Store className="h-3.5 w-3.5 text-muted-foreground" />
                <select
                  value={selectedStore}
                  onChange={e => setSelectedStore(e.target.value)}
                  className={cn(
                    'h-8 rounded-lg border border-input bg-background px-2.5 text-xs text-foreground',
                    'focus:outline-none focus:ring-2 focus:ring-ring',
                  )}
                >
                  <option value="0">All Stores</option>
                  {stores.map(s => (
                    <option key={s._id} value={s._id}>{s.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Group filter */}
            {productGroups.length > 0 && (
              <div className="flex items-center gap-1.5">
                <Filter className="h-3.5 w-3.5 text-muted-foreground" />
                <select
                  value={selectedPG}
                  onChange={e => setSelectedPG(e.target.value)}
                  className={cn(
                    'h-8 rounded-lg border border-input bg-background px-2.5 text-xs text-foreground',
                    'focus:outline-none focus:ring-2 focus:ring-ring',
                  )}
                >
                  <option value="">All Groups</option>
                  {productGroups.map(g => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Date range picker */}
            <DateRangeFilter
              fromDate={fromDate}
              toDate={toDate}
              onFromChange={setFromDate}
              onToChange={setToDate}
              onApply={() => setFetchTrigger(t => t + 1)}
              loading={loading}
              lastUpdated={lastUpdated}
              initialPreset="Prior week"
            />

            {/* Export */}
            <Button
              variant="outline"
              size="sm"
              className="h-8 gap-1.5 text-xs"
              onClick={handleExport}
            >
              <Download className="h-3.5 w-3.5" />
              Export
            </Button>
          </div>
        </div>
      </div>

      {/* ── Token health banner ─────────────────────────────────────────── */}
      {tokenHealth && !tokenHealth.healthy && (
        <TokenAlert
          expiredStores={tokenHealth.expiredStores}
          zeroPriceCount={tokenHealth.zeroPriceCount}
          refreshing={refreshingTokens}
          onRefresh={handleRefreshTokens}
          onDismiss={() => setTokenHealth(h => h ? { ...h, healthy: true } : h)}
        />
      )}

      {/* ── Body ────────────────────────────────────────────────────────── */}
      {loading ? (
        <div className="p-4 lg:p-6 space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Skeleton className="h-52 rounded-xl" />
            <Skeleton className="h-52 rounded-xl" />
          </div>
          <Skeleton className="h-96 rounded-xl" />
        </div>
      ) : (
        <div className="flex-1 space-y-4 p-4 lg:p-6">

          {/* KPI cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard
              label="Total Profit"
              value={fmtMAD(kpi.totalProfit)}
              sub={`${filtered.length} SKUs tracked`}
              icon={TrendingUp}
              color="green"
              trend={kpi.totalProfit > 0 ? 'up' : kpi.totalProfit < 0 ? 'down' : 'neutral'}
            />
            <KpiCard
              label="Total Orders"
              value={kpi.tOrders.toLocaleString()}
              sub={`${kpi.delivered.toLocaleString()} delivered`}
              icon={ShoppingCart}
              color="blue"
            />
            <KpiCard
              label="Delivery Rate"
              value={fmtPct(kpi.deliveryRate)}
              sub="of total orders"
              icon={Percent}
              color={kpi.deliveryRate >= 70 ? 'green' : kpi.deliveryRate >= 50 ? 'orange' : 'red'}
              trend={kpi.deliveryRate >= 70 ? 'up' : 'down'}
            />
            <KpiCard
              label="Failed Rate"
              value={fmtPct(kpi.failedRate)}
              sub={`${kpi.failed.toLocaleString()} failed orders`}
              icon={XCircle}
              color={kpi.failedRate <= 10 ? 'green' : kpi.failedRate <= 20 ? 'orange' : 'red'}
              trend={kpi.failedRate <= 10 ? 'up' : 'down'}
            /></div>

          {/* Mid row: funnel + top products chart */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

            {/* Order funnel */}
            <div className="rounded-xl border border-border bg-card p-5">
              <p className="mb-4 text-sm font-semibold text-foreground">Order Funnel</p>
              <div className="space-y-3">
                {funnelData.map(row => (
                  <div key={row.label} className="flex items-center gap-2">
                    <span className="w-20 shrink-0 text-xs text-muted-foreground">{row.label}</span>
                    <div className="flex-1 h-2 overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${row.pct}%`, backgroundColor: row.color }}
                      />
                    </div>
                    <span className="w-14 shrink-0 text-right text-xs font-semibold text-foreground tabular-nums">{row.count.toLocaleString()}</span>
                    <span className="w-12 shrink-0 text-right text-xs text-muted-foreground tabular-nums">{fmtPct(row.pct)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Top 8 products bar chart */}
            <div className="rounded-xl border border-border bg-card p-5">
              <p className="mb-4 text-sm font-semibold text-foreground">Top 8 Products by Profit</p>
              {top8.length === 0 ? (
                <EmptyState emoji="📦" message="No profitable products in this period" />
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart
                      data={top8}
                      layout="vertical"
                      margin={{ left: 0, right: 70, top: 0, bottom: 0 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="#F0F0F0"
                        horizontal={false}
                      />
                      <XAxis
                        type="number"
                        tick={{ fontSize: 9, fill: '#9CA3AF' }}
                        tickFormatter={v => `${(v / 1000).toFixed(0)}k`}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        type="category"
                        dataKey="shortName"
                        width={135}
                        tick={{ fontSize: 10, fill: '#6B7280' }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <Tooltip content={<ChartTooltip />} />
                      <Bar dataKey="totalProfit" radius={[0, 4, 4, 0]}>
                        {top8.map((p, i) => (
                          <Cell
                            key={i}
                            fill={
                              (p.roi || 0) >= 50
                                ? '#1D9E75'
                                : (p.roi || 0) >= 20
                                ? '#378ADD'
                                : '#EF9F27'
                            }
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  <div className="sa__legend">
                    <span className="sa__legend-item">
                      <span className="sa__legend-dot" style={{ background: '#1D9E75' }} />
                      ROI ≥ 50%
                    </span>
                    <span className="sa__legend-item">
                      <span className="sa__legend-dot" style={{ background: '#378ADD' }} />
                      ROI ≥ 20%
                    </span>
                    <span className="sa__legend-item">
                      <span className="sa__legend-dot" style={{ background: '#EF9F27' }} />
                      ROI &lt; 20%
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Stores ranking + Product groups row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

            {/* Stores ranking */}
            {selectedStore === '0' && topStores.length > 1 && (
              <div className="rounded-xl border border-border bg-card p-5">
                <p className="mb-4 text-sm font-semibold text-foreground">Stores Ranking by Profit</p>
                <ResponsiveContainer width="100%" height={Math.max(160, topStores.length * 32)}>
                  <BarChart
                    data={topStores}
                    layout="vertical"
                    margin={{ left: 0, right: 150, top: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" horizontal={false} />
                    <XAxis
                      type="number"
                      tick={{ fontSize: 9, fill: '#9CA3AF' }}
                      tickFormatter={v => `${(v / 1000).toFixed(0)}k`}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={120}
                      tick={{ fontSize: 10, fill: '#6B7280' }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      formatter={(value: any, _: any, props: any) => [
                        `${fmtMAD(value)}  ·  ${props.payload.delivered} orders`,
                        'Profit',
                      ]}
                      contentStyle={{ fontSize: '0.75rem', borderRadius: 8, border: '0.5px solid #E2E4E9' }}
                    />
                    <Bar dataKey="profit" radius={[0, 4, 4, 0]}>
                      {topStores.map((s, i) => (
                        <Cell
                          key={i}
                          fill={s.profit < 0 ? '#E24B4A' : i === 0 ? '#1D9E75' : i === 1 ? '#378ADD' : '#EF9F27'}
                        />
                      ))}
                      <LabelList
                        dataKey="profit"
                        position="right"
                        content={(props: any) => {
                          const { x, y, width, height, index } = props;
                          const s = topStores[index];
                          if (!s) return null;
                          return (
                            <text
                              x={(x as number) + (width as number) + 8}
                              y={(y as number) + (height as number) / 2}
                              dominantBaseline="middle"
                              fontSize={9.5}
                              fill="#1A1D23"
                            >
                              <tspan fontWeight="500">{fmtMAD(s.profit)}</tspan>
                              <tspan fill="#9CA3AF"> · {s.delivered} ord</tspan>
                            </text>
                          );
                        }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div className="sa__legend">
                  <span className="sa__legend-item"><span className="sa__legend-dot" style={{ background: '#1D9E75' }} />#1</span>
                  <span className="sa__legend-item"><span className="sa__legend-dot" style={{ background: '#378ADD' }} />#2</span>
                  <span className="sa__legend-item"><span className="sa__legend-dot" style={{ background: '#EF9F27' }} />Others</span>
                  <span className="sa__legend-item"><span className="sa__legend-dot" style={{ background: '#E24B4A' }} />Negative</span>
                </div>
              </div>
            )}

            {/* Product groups */}
            <div className="rounded-xl border border-border bg-card p-5">
              <p className="mb-4 text-sm font-semibold text-foreground">Product Groups by Profit</p>
              {topPG.length === 0 ? (
                <EmptyState emoji="📂" message="No product group data available" />
              ) : (
                <div className="space-y-3">
                  {topPG.map((g, i) => (
                    <div key={i}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-foreground truncate">{g.name}</span>
                        <span className="text-xs font-bold text-foreground tabular-nums shrink-0">{fmtMAD(g.profit)}</span>
                      </div>
                      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                        <div
                          className="h-full rounded-full bg-primary transition-all duration-500"
                          style={{ width: `${(g.profit / maxPGProfit) * 100}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>

          {/* Product segments */}
          <div className="rounded-xl border border-border bg-card overflow-hidden">

            {/* Tab navigation bar */}
            <div className="flex items-center gap-0.5 overflow-x-auto border-b border-border px-4 py-0 scrollbar-none">
              {([
                {
                  key: 'plan', label: 'Action Plan', icon: '📋',
                  badge: actionPlan.filter(a => a.priority === 1).length || null,
                  badgeVariant: 'destructive' as const,
                },
                { key: 'stars',   label: `Stars`,        icon: '★',  count: stars.length,    badgeVariant: 'success' as const },
                ...(bleeding.length > 0 ? [{ key: 'bleeding', label: `Bleeding`, icon: '⚠', count: bleeding.length, badgeVariant: 'destructive' as const }] : []),
                { key: 'gems',    label: `Hidden Gems`,  icon: '◆',  count: gems.length,     badgeVariant: 'info' as const },
                { key: 'rising',  label: `Rising`,       icon: '↑',  count: rising.length,   badgeVariant: 'success' as const },
                { key: 'thin',    label: `Thin Margin`,  icon: '◎',  count: thin.length,     badgeVariant: 'warning' as const },
                { key: 'watched', label: `Watching`,     icon: '📌', count: trackedSkus.length, badgeVariant: 'purple' as const },
              ] as any[]).map(tab => {
                const isActive = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={cn(
                      'relative flex shrink-0 items-center gap-1.5 px-3 py-3 text-xs font-medium transition-colors',
                      'focus:outline-none',
                      isActive
                        ? 'text-foreground'
                        : 'text-muted-foreground hover:text-foreground'
                    )}
                  >
                    <span>{tab.icon}</span>
                    <span>{tab.label}</span>
                    {(tab.count !== undefined && tab.count > 0) && (
                      <Badge variant={tab.badgeVariant} className="h-4 px-1 text-[10px]">
                        {tab.count}
                      </Badge>
                    )}
                    {tab.badge != null && tab.badge > 0 && (
                      <Badge variant="destructive" className="h-4 px-1 text-[10px]">
                        {tab.badge}
                      </Badge>
                    )}
                    {/* Active indicator */}
                    {isActive && (
                      <span className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full bg-primary" />
                    )}
                  </button>
                );
              })}
            </div>

            {/* ─── Legacy sa__tabs hidden ─── */}
            <div className="hidden">
              <button
                className={`sa__tab sa__tab--watched ${activeTab === 'watched' ? 'sa__tab--active' : ''}`}
                onClick={() => setActiveTab('watched')}
              >
                📌 Watching
                {trackedSkus.length > 0 && (
                  <span className="sa__tab-watch-count">{trackedSkus.length}</span>
                )}
              </button>
            </div>

            {/* ── Action Plan ───────────────────────────────────────────── */}
            {activeTab === 'plan' && (() => {
              const urgentItems  = actionPlan.filter(a => a.priority === 1);
              const todayItems   = actionPlan.filter(a => a.priority === 2);
              const weekItems    = actionPlan.filter(a => a.priority === 3);
              const totalImpact  = actionPlan.reduce((s, a) => s + a.impact, 0);
              const totalMinutes = actionPlan.reduce((s, a) => s + parseInt(a.effort), 0);

              const sectionCfg = {
                1: { label: '🔴 URGENT — Do these first',   sub: 'Stop losing money now',       cls: 'sa__plan-section--urgent' },
                2: { label: '🟡 TODAY — High return',        sub: 'Quick wins with big impact',  cls: 'sa__plan-section--today'  },
                3: { label: '🟢 THIS WEEK — Grow',           sub: 'Build for next month',        cls: 'sa__plan-section--week'   },
              };

              const typeColor: Record<ActionType, string> = {
                'stop-loss':   '#E24B4A',
                'dead-star':   '#E24B4A',
                'fix-returns': '#EF9F27',
                'almost-star': '#378ADD',
                'push-gem':    '#1D9E75',
                'price-up':    '#1D9E75',
                'nurture':     '#378ADD',
              };

              if (actionPlan.length === 0) return (
                <EmptyState
                  emoji="✅"
                  message="All products healthy"
                  sub="No actions needed right now. Come back after the next data refresh."
                />
              );

              return (
                <div>
                  {/* Summary banner */}
                  <SectionBanner stats={[
                    { label: 'actions today',    value: actionPlan.length,        color: 'default' },
                    { label: 'MAD at stake',     value: fmtMAD(totalImpact),      color: 'amber'   },
                    { label: 'estimated time',   value: `~${totalMinutes} min`,   color: 'default' },
                    ...(urgentItems.length > 0 ? [{ label: 'urgent — act now', value: urgentItems.length, color: 'red' as const }] : []),
                  ]} />

                  {/* Sections */}
                  {([
                    { priority: 1 as ActionPriority, items: urgentItems },
                    { priority: 2 as ActionPriority, items: todayItems  },
                    { priority: 3 as ActionPriority, items: weekItems   },
                  ] as { priority: ActionPriority; items: ActionItem[] }[]).map(({ priority, items }) => {
                    if (items.length === 0) return null;
                    const cfg = sectionCfg[priority];
                    return (
                      <div key={priority} className="px-4 pt-4 last:pb-4">
                        <div className="flex items-baseline gap-2 mb-3">
                          <span className="text-xs font-bold text-foreground">{cfg.label}</span>
                          <span className="text-xs text-muted-foreground">{cfg.sub}</span>
                        </div>
                        {items.map(item => {
                          // Find most recent log for this product+type
                          const log = actionLogs
                            .filter(l => l.productId === item.product._id && l.actionType === item.type)
                            .sort((a: any, b: any) => new Date(b.takenAt).getTime() - new Date(a.takenAt).getTime())[0];

                          const daysSince = log
                            ? Math.floor((Date.now() - new Date(log.takenAt).getTime()) / (1000 * 60 * 60 * 24))
                            : null;

                          // Compute impact delta if log exists
                          const p = item.product;
                          const deltaOrders = log ? (p.deliveredOrders || 0) - log.baseline.deliveredOrders : null;
                          const deltaPPP    = log ? (p.profitPerProduct || 0) - log.baseline.profitPerProduct : null;
                          const deltaProfit = log ? (p.totalProfit || 0) - log.baseline.totalProfit : null;

                          // Auto-resolve detection
                          let resolved = false;
                          if (log) {
                            if (item.type === 'stop-loss' && (p.profitPerProduct || 0) >= 0) resolved = true;
                            if (item.type === 'dead-star' && (p.progressDeliveredOrdersAfter || 0) > 0) resolved = true;
                            if (item.type === 'almost-star' && stars.some(s => s._id === p._id)) resolved = true;
                            if (item.type === 'push-gem' && (p.deliveredOrders || 0) >= 10) resolved = true;
                            if (item.type === 'price-up' && deltaPPP !== null && deltaPPP >= 5) resolved = true;
                            if (item.type === 'fix-returns' && (p.returnedOrders || 0) < log.baseline.returnedOrders) resolved = true;
                            if (item.type === 'nurture' && stars.some(s => s._id === p._id)) resolved = true;
                          }

                          return (
                            <div key={item.id} className={cn('flex gap-3 rounded-xl border p-3 mb-2 transition-colors', resolved ? 'border-emerald-200 bg-emerald-50/50 dark:border-emerald-900 dark:bg-emerald-950/20' : 'border-border bg-card')}>
                              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold"
                                style={{ background: resolved ? '#1D9E7518' : typeColor[item.type] + '18', color: resolved ? '#1D9E75' : typeColor[item.type] }}>
                                {resolved ? '✓' : `#${item.num}`}
                              </div>
                              <div className="text-base shrink-0 mt-0.5">{item.icon}</div>
                              <div className="flex-1 min-w-0">
                                <div className="text-xs font-semibold text-foreground leading-snug mb-1"
                                  style={{ textDecoration: resolved ? 'line-through' : 'none', color: resolved ? '#9CA3AF' : undefined }}>
                                  {item.title}
                                </div>
                                {!log && <div className="text-[11px] text-muted-foreground leading-relaxed">{item.detail}</div>}
                                {item.product && (
                                  <div className="text-[10px] text-muted-foreground font-mono mt-0.5">
                                    {p.url ? <a href={p.url} target="_blank" rel="noopener noreferrer">{p.Sku}</a> : p.Sku}
                                    {' · '}{p.store?.name}
                                  </div>
                                )}
                                {!log && item.product && (
                                  <div style={{ marginTop: '0.4rem' }}>
                                    <PriceWidget
                                      storeId={(p.store as any)?._id || (p.store as any) || selectedStore}
                                      sku={p.Sku}
                                      token={contextStore.token}
                                      showProfit
                                      buyingRate={p.productGroup?.buyingRate || undefined}
                                      ourFees={(() => { const t = (p.productGroup?.transportFees||0)+(p.productGroup?.storageFees||0)+(p.productGroup?.packageFees||0); return t > 0 ? t : undefined; })()}
                                    />
                                  </div>
                                )}
                                {/* Impact tracking panel */}
                                {log && (
                                  <div className={`sa__plan-track ${resolved ? 'sa__plan-track--resolved' : 'sa__plan-track--pending'}`}>
                                    <div className="sa__plan-track-hdr">
                                      {resolved
                                        ? `✅ Resolved — applied ${daysSince === 0 ? 'today' : `${daysSince}d ago`}`
                                        : `⏳ Applied ${daysSince === 0 ? 'today' : `${daysSince}d ago`} — monitoring impact`}
                                      <button className="sa__plan-track-undo" onClick={() => deleteActionLog(log._id)} title="Remove tracking">×</button>
                                    </div>
                                    <div className="sa__plan-track-metrics">
                                      <div className="sa__plan-track-col">
                                        <div className="sa__plan-track-lbl">When applied</div>
                                        <div className="sa__plan-track-val">{log.baseline.deliveredOrders} orders</div>
                                        <div className="sa__plan-track-val">{fmtMAD(log.baseline.profitPerProduct)}/unit</div>
                                        <div className="sa__plan-track-val">{fmtMAD(log.baseline.totalProfit)} total</div>
                                      </div>
                                      <div className="sa__plan-track-arrow">→</div>
                                      <div className="sa__plan-track-col">
                                        <div className="sa__plan-track-lbl">Now</div>
                                        <div className="sa__plan-track-val" style={{ color: deltaOrders !== null && deltaOrders > 0 ? '#1D9E75' : deltaOrders !== null && deltaOrders < 0 ? '#E24B4A' : undefined }}>
                                          {p.deliveredOrders} orders {deltaOrders !== null && deltaOrders !== 0 && `(${deltaOrders > 0 ? '+' : ''}${deltaOrders})`}
                                        </div>
                                        <div className="sa__plan-track-val" style={{ color: deltaPPP !== null && deltaPPP > 0 ? '#1D9E75' : deltaPPP !== null && deltaPPP < 0 ? '#E24B4A' : undefined }}>
                                          {fmtMAD(p.profitPerProduct || 0)}/unit {deltaPPP !== null && deltaPPP !== 0 && `(${deltaPPP > 0 ? '+' : ''}${fmtMAD(deltaPPP)})`}
                                        </div>
                                        <div className="sa__plan-track-val" style={{ color: deltaProfit !== null && deltaProfit > 0 ? '#1D9E75' : deltaProfit !== null && deltaProfit < 0 ? '#E24B4A' : undefined }}>
                                          {fmtMAD(p.totalProfit || 0)} {deltaProfit !== null && deltaProfit !== 0 && `(${deltaProfit > 0 ? '+' : ''}${fmtMAD(deltaProfit)})`}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                              <div className="flex shrink-0 flex-col items-end gap-1.5 ml-2">
                                {!log ? (
                                  <>
                                    <div className="text-xs font-bold tabular-nums" style={{ color: typeColor[item.type] }}>
                                      {item.type === 'stop-loss' || item.type === 'fix-returns'
                                        ? `−${fmtMAD(item.impact)}`
                                        : `+${fmtMAD(Math.round(item.impact))}`}
                                    </div>
                                    <div className="text-[10px] text-muted-foreground">
                                      {item.type === 'stop-loss' || item.type === 'fix-returns' ? 'currently losing' : 'potential gain'}
                                    </div>
                                    <div className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">{item.effort}</div>
                                    <button className="rounded-md border border-border bg-background px-2 py-1 text-[10px] font-medium text-muted-foreground hover:bg-accent transition-colors" onClick={() => setActiveTab(item.actionTab)}>View →</button>
                                    <button
                                      className="rounded-md bg-emerald-500 px-2 py-1 text-[10px] font-semibold text-white hover:bg-emerald-600 disabled:opacity-50 transition-colors"
                                      disabled={savingAction === item.id}
                                      onClick={() => markActionDone(item)}
                                    >
                                      {savingAction === item.id ? '…' : '✓ Done'}
                                    </button>
                                  </>
                                ) : (
                                  <button className="rounded-md border border-border bg-background px-2 py-1 text-[10px] font-medium text-muted-foreground hover:bg-accent transition-colors" onClick={() => setActiveTab(item.actionTab)}>View →</button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}

                  {/* History: resolved actions no longer in plan */}
                  {(() => {
                    const resolvedHistory = actionLogs.filter(log => {
                      // Show logs whose product is NOT currently generating a plan item of the same type
                      return !actionPlan.some(item => item.product._id === log.productId && item.type === log.actionType);
                    });
                    if (resolvedHistory.length === 0) return null;
                    return (
                      <div className="px-4 pt-4 pb-4 opacity-60">
                        <div className="flex items-baseline gap-2 mb-3">
                          <span className="text-xs font-bold text-muted-foreground">📁 History — No Longer Active</span>
                          <span className="text-xs text-muted-foreground">These problems are gone from your current plan</span>
                        </div>
                        {resolvedHistory.map((log: any) => {
                          const daysSince = Math.floor((Date.now() - new Date(log.takenAt).getTime()) / (1000 * 60 * 60 * 24));
                          // Find current product metrics
                          const currentProduct = filtered.find(p => p._id === log.productId);
                          const deltaOrders = currentProduct ? (currentProduct.deliveredOrders || 0) - log.baseline.deliveredOrders : null;
                          const deltaProfit = currentProduct ? (currentProduct.totalProfit || 0) - log.baseline.totalProfit : null;
                          return (
                            <div key={log._id} className="flex gap-3 rounded-xl border border-border bg-muted/30 p-3 mb-2 opacity-60">
                              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold" style={{ background: '#1D9E7518', color: '#1D9E75' }}>✓</div>
                              <div className="text-base shrink-0 mt-0.5">📁</div>
                              <div className="flex-1 min-w-0">
                                <div className="text-xs font-semibold text-foreground leading-snug mb-1" style={{ color: '#9CA3AF', textDecoration: 'line-through' }}>
                                  {log.actionTitle}
                                </div>
                                <div className="text-[10px] text-muted-foreground font-mono mt-0.5">
                                  Applied {daysSince === 0 ? 'today' : `${daysSince} day${daysSince > 1 ? 's' : ''} ago`}
                                  {deltaOrders !== null && ` · Orders: ${log.baseline.deliveredOrders} → ${currentProduct?.deliveredOrders ?? '?'} (${deltaOrders >= 0 ? '+' : ''}${deltaOrders})`}
                                  {deltaProfit !== null && ` · Profit: ${fmtMAD(log.baseline.totalProfit)} → ${fmtMAD(currentProduct?.totalProfit || 0)} (${deltaProfit >= 0 ? '+' : ''}${fmtMAD(deltaProfit)})`}
                                </div>
                              </div>
                              <div className="flex shrink-0 flex-col items-end gap-1.5 ml-2">
                                <button className="sa__plan-track-undo" style={{ fontSize: '0.75rem' }} onClick={() => deleteActionLog(log._id)} title="Remove from history">×</button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              );
            })()}

            {/* ── Stars ─────────────────────────────────────────────────── */}
            {activeTab === 'stars' && (
              <>
                {/* View toggle */}
                <div className="flex gap-1.5 px-4 pt-3">
                  {(['health', 'simulator'] as const).map(view => (
                    <button
                      key={view}
                      onClick={() => setStarsView(view)}
                      className={cn(
                        'rounded-lg px-3 py-1.5 text-xs font-medium transition-colors',
                        starsView === view
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground hover:text-foreground'
                      )}
                    >
                      {view === 'health' ? 'Health Monitor' : 'Price Simulator'}
                    </button>
                  ))}
                </div>

                {/* ── Health Monitor ── */}
                {starsView === 'health' && (
                  stars.length === 0 ? (
                    <EmptyState emoji="★" message="No Stars yet" sub="Criteria: ROI ≥ 25% and ≥ 10 delivered orders" />
                  ) : (
                    <>
                      {/* Urgent / warning summary banner */}
                      {(() => {
                        const urgentCount  = stars.filter(p => getUrgency(dailyData[p._id]).level === 'urgent').length;
                        const warningCount = stars.filter(p => getUrgency(dailyData[p._id]).level === 'warning').length;
                        if (urgentCount === 0 && warningCount === 0) return null;
                        return (
                          <div className="flex flex-wrap gap-2 px-4 py-2 bg-red-50 border-b border-red-200 dark:bg-red-950/20 dark:border-red-900">
                            {urgentCount > 0 && (
                              <span className="text-xs font-semibold text-red-700 dark:text-red-400">🔴 {urgentCount} Star{urgentCount > 1 ? 's' : ''} — 3+ days empty pipeline, act immediately</span>
                            )}
                            {warningCount > 0 && (
                              <span className="text-xs font-semibold text-orange-700 dark:text-orange-400">🟠 {warningCount} Star{warningCount > 1 ? 's' : ''} — 2 days empty, investigate now</span>
                            )}
                          </div>
                        );
                      })()}

                      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 p-4">
                        {[...stars]
                          // Sort: urgent first, then warning, then watch, then by health severity
                          .sort((a, b) => {
                            const urgOrder = { urgent: 0, warning: 1, watch: 2, none: 3 };
                            const ua = getUrgency(dailyData[a._id]).level;
                            const ub = getUrgency(dailyData[b._id]).level;
                            if (ua !== ub) return urgOrder[ua] - urgOrder[ub];
                            const hOrder = { alert: 0, declining: 1, stable: 2, growing: 3 };
                            return hOrder[getHealthStatus(a)] - hOrder[getHealthStatus(b)];
                          })
                          .map(p => {
                            const health  = getHealthStatus(p);
                            const hcfg    = healthConfig[health];
                            const chart   = dailyData[p._id];
                            const isLoading = loadingDaily[p._id];
                            const { level: urgLevel, zeroTail, recentTrend } = getUrgency(chart);
                            const ucfg    = urgencyConfig[urgLevel];

                            // Card border driven by urgency if active, else by health
                            const borderColor = urgLevel !== 'none' ? ucfg.border : hcfg.border;

                            const before  = p.progressDeliveredOrdersBefore || 0;
                            const after   = p.progressDeliveredOrdersAfter  || 0;
                            const pct     = p.progressDeliveredOrders        || 0;

                            const recs = getRecommendations(p, health, urgLevel, zeroTail, recentTrend);

                            return (
                              <div
                                key={p._id}
                                className="rounded-xl border border-border bg-card p-4 flex flex-col gap-3 shadow-card hover:shadow-card-md transition-all duration-200"
                                style={{ borderColor, borderWidth: urgLevel === 'urgent' ? '1.5px' : '0.5px' }}
                              >
                                {/* Urgency alert strip */}
                                {urgLevel !== 'none' && chart && (
                                  <div
                                    className="-mx-4 -mt-4 mb-0 rounded-t-xl border-b px-3 py-2 text-xs font-semibold"
                                    style={{ background: ucfg.bg, color: ucfg.color, borderColor: ucfg.border }}
                                  >
                                    {urgLevel === 'urgent'
                                      ? `🔴 ${zeroTail} days with zero pipeline — no delivered, shipped, or pending orders — act NOW`
                                      : urgLevel === 'warning'
                                        ? `🟠 2 days with empty pipeline — nothing delivered, shipped, or pending — investigate immediately`
                                        : zeroTail === 1
                                          ? `🟡 No orders in pipeline today — watch closely, if tomorrow is also zero act fast`
                                          : `🟡 Orders declining in the last 5 days — investigate before it gets worse`
                                    }
                                  </div>
                                )}

                                {/* Loading strip (while auto-fetching) */}
                                {isLoading && !chart && (
                                  <div className="text-xs text-muted-foreground animate-pulse">Checking daily health…</div>
                                )}

                                {/* Header */}
                                <div className="flex items-start justify-between gap-2">
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div className="flex items-start justify-between gap-2" style={{ marginBottom: '0.1rem' }}>
                                      <span
                                        onClick={e => handleNameClick(e, p.imgUri)}
                                        style={{ cursor: p.imgUri ? 'pointer' : 'default', fontWeight: 600, fontSize: '0.8rem' }}
                                        title={p.imgUri ? 'Click to see product image' : ''}
                                      >
                                        {truncate(p.name, 32)}
                                      </span>
                                    </div>
                                    <div className="text-xs text-muted-foreground">{p.url ? <a href={p.url} target="_blank" rel="noopener noreferrer">{p.Sku}</a> : p.Sku} · {p.store?.name}</div>
                                  </div>
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.3rem' }}>
                                    <div
                                      className="rounded-full border px-2 py-0.5 text-[10px] font-semibold"
                                      style={{ background: hcfg.bg, color: hcfg.color, borderColor: hcfg.border }}
                                    >
                                      {hcfg.label}
                                    </div>
                                    {chart && recentTrend !== 'flat' && (
                                      <div
                                        className="rounded-full border px-2 py-0.5 text-[10px] font-semibold"
                                        style={{
                                          background: trendConfig[recentTrend].bg,
                                          color: trendConfig[recentTrend].color,
                                          borderColor: trendConfig[recentTrend].border,
                                          fontSize: '0.65rem',
                                        }}
                                      >
                                        {trendConfig[recentTrend].label}
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {/* Period trend */}
                                <div className="flex items-center justify-between gap-2">
                                  <div className="flex flex-col">
                                    <span className="block text-[10px] text-muted-foreground uppercase tracking-wide">First half</span>
                                    <span className="block text-sm font-bold text-foreground mt-0.5">{before} orders</span>
                                  </div>
                                  <div className="text-sm font-bold" style={{ color: hcfg.color }}>
                                    {pct > 0 ? `+${pct.toFixed(0)}%` : `${pct.toFixed(0)}%`}
                                  </div>
                                  <div className="flex flex-col" style={{ textAlign: 'right' }}>
                                    <span className="block text-[10px] text-muted-foreground uppercase tracking-wide">Second half</span>
                                    <span className="block text-sm font-bold text-foreground mt-0.5">{after} orders</span>
                                  </div>
                                </div>

                                {/* Key metrics */}
                                <div className="grid grid-cols-3 gap-1 rounded-lg bg-muted/40 px-2 py-2" style={{ margin: '0.5rem 0' }}>
                                  <div>
                                    <span className="block text-[10px] text-muted-foreground uppercase tracking-wide">Delivered</span>
                                    <span className="block text-sm font-bold text-foreground mt-0.5">{p.deliveredOrders}</span>
                                  </div>
                                  <div>
                                    <span className="block text-[10px] text-muted-foreground uppercase tracking-wide">Profit/unit</span>
                                    <span className="block text-sm font-bold text-foreground mt-0.5" style={{ color: '#1D9E75' }}>
                                      {fmtMAD(p.profitPerProduct || 0)}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="block text-[10px] text-muted-foreground uppercase tracking-wide">ROI</span>
                                    <span className="block text-sm font-bold text-foreground mt-0.5" style={{ color: '#1D9E75' }}>
                                      {(p.roi || 0).toFixed(0)}%
                                    </span>
                                  </div>
                                  <div>
                                    <span className="block text-[10px] text-muted-foreground uppercase tracking-wide">Total Profit</span>
                                    <span className="block text-sm font-bold text-foreground mt-0.5">{fmtMAD(p.totalProfit || 0)}</span>
                                  </div>
                                </div>

                                {/* Stock info */}
                                {(p.productGroup?.stockAvailable != null || p.productGroup?.manualStock != null) && (
                                  <div className="flex items-center gap-2 flex-wrap">
                                    {(p.productGroup?.stockAvailable ?? 0) > 0 && (
                                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-blue-600 bg-blue-500/10 rounded-full px-2 py-0.5">
                                        📦 {p.productGroup!.stockAvailable} group stock
                                      </span>
                                    )}
                                    {p.productGroup?.manualStock != null && (
                                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-violet-600 bg-violet-500/10 rounded-full px-2 py-0.5">
                                        🏭 {p.productGroup!.manualStock} warehouse
                                      </span>
                                    )}
                                    {p.productGroup?.damagedStock != null && (p.productGroup.damagedStock ?? 0) > 0 && (
                                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-600 bg-amber-500/10 rounded-full px-2 py-0.5">
                                        ⚠ {p.productGroup!.damagedStock} damaged
                                      </span>
                                    )}
                                  </div>
                                )}

                                {/* Daily sparkline */}
                                {chart ? (
                                  <div style={{ marginBottom: '0.5rem' }}>
                                    <div className="block text-[10px] text-muted-foreground uppercase tracking-wide" style={{ marginBottom: '0.25rem' }}>
                                      Daily orders by creation date
                                      {zeroTail > 0 && (
                                        <span style={{ color: '#E24B4A', marginLeft: '0.5rem' }}>
                                          ({zeroTail} day{zeroTail > 1 ? 's' : ''} with no new orders)
                                        </span>
                                      )}
                                    </div>
                                    <ResponsiveContainer width="100%" height={80}>
                                      <LineChart
                                        data={chart.map(d => ({ ...d, activeOrders: activeOrders(d) }))}
                                        margin={{ top: 2, right: 4, left: -30, bottom: 0 }}
                                      >
                                        <XAxis dataKey="date" hide />
                                        <YAxis hide />
                                        <Tooltip
                                          content={({ active, payload, label }: any) =>
                                            active && payload?.length ? (
                                              <div className="sa__tooltip">
                                                <div style={{ color: '#9CA3AF', fontSize: '0.65rem' }}>{label}</div>
                                                {payload.map((entry: any) => (
                                                  <div key={entry.dataKey} style={{ color: entry.color }}>
                                                    {entry.name}: {entry.value}
                                                  </div>
                                                ))}
                                              </div>
                                            ) : null
                                          }
                                        />
                                        <Line
                                          type="monotone"
                                          dataKey="activeOrders"
                                          name="In pipeline"
                                          stroke="#9CA3AF"
                                          strokeWidth={1}
                                          strokeDasharray="3 3"
                                          dot={false}
                                        />
                                        <Line
                                          type="monotone"
                                          dataKey="deliveredOrders"
                                          name="Delivered"
                                          stroke={urgLevel === 'urgent' ? '#E24B4A' : hcfg.color}
                                          strokeWidth={1.5}
                                          dot={(props: any) => {
                                            const { cx, cy, payload } = props;
                                            const hasActivity = activeOrders(payload) > 0;
                                            return hasActivity
                                              ? <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={2} fill={hcfg.color} stroke="none" />
                                              : <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={3} fill="#E24B4A" stroke="none" />;
                                          }}
                                        />
                                      </LineChart>
                                    </ResponsiveContainer>
                                    <div style={{ fontSize: '0.62rem', color: '#9CA3AF' }}>
                                      Orders grouped by creation date · Solid = delivered · Dashed = in pipeline · Red dot = no orders created that day
                                    </div>
                                  </div>
                                ) : !isLoading ? (
                                  <button
                                    className="sa__load-chart-btn"
                                    onClick={() => loadDailyChart(p._id)}
                                  >
                                    View daily orders chart
                                  </button>
                                ) : null}

                                {/* Price */}
                                <PriceWidget
                                  storeId={(p.store as any)?._id || (p.store as any) || selectedStore}
                                  sku={p.Sku}
                                  token={contextStore.token}
                                  showProfit
                                  buyingRate={p.productGroup?.buyingRate || undefined}
                                  ourFees={(() => { const t = (p.productGroup?.transportFees||0)+(p.productGroup?.storageFees||0)+(p.productGroup?.packageFees||0); return t > 0 ? t : undefined; })()}
                                />

                                {/* Recommendations */}
                                <div className="rounded-lg bg-muted/50 p-3 space-y-1">
                                  <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1.5">What to do</div>
                                  {recs.map((r, i) => (
                                    <div key={i} className="flex gap-1.5 text-xs text-muted-foreground leading-relaxed">
                                      <span style={{ color: urgLevel !== 'none' ? ucfg.color : hcfg.color, marginRight: '0.4rem' }}>›</span>
                                      {r}
                                    </div>
                                  ))}
                                </div>

                                {/* Track button */}
                                {isTracked(p._id) ? (
                                  <div className="sa__track-active">
                                    <span className="sa__track-active-label">📌 Watching</span>
                                    <span className="sa__track-reason-pill">
                                      {trackedSkus.find(t => t.productId === p._id)?.reason}
                                    </span>
                                    <button className="sa__track-remove" onClick={() => removeFromTracking(p._id)}>✕ Remove</button>
                                  </div>
                                ) : addTrackingFor === p._id ? (
                                  <div className="sa__track-form">
                                    <div className="sa__track-form-presets">
                                      {TRACKING_PRESETS.map(preset => (
                                        <button
                                          key={preset}
                                          className={`sa__track-preset ${trackingReason === preset ? 'sa__track-preset--active' : ''}`}
                                          onClick={() => setTrackingReason(preset)}
                                        >
                                          {preset}
                                        </button>
                                      ))}
                                    </div>
                                    <input
                                      className="sa__track-input"
                                      placeholder="Or type a custom reason…"
                                      value={trackingReason}
                                      onChange={e => setTrackingReason(e.target.value)}
                                      onKeyDown={e => e.key === 'Enter' && addToTracking(p._id, trackingReason)}
                                    />
                                    <div className="sa__track-form-actions">
                                      <button className="sa__track-confirm" onClick={() => addToTracking(p._id, trackingReason)}>
                                        Add to Watching
                                      </button>
                                      <button className="sa__track-cancel" onClick={() => { setAddTrackingFor(null); setTrackingReason(''); }}>
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <button className="sa__track-btn" onClick={() => { setAddTrackingFor(p._id); setTrackingReason(''); }}>
                                    📌 Track this SKU
                                  </button>
                                )}
                              </div>
                            );
                          })}
                      </div>
                    </>
                  )
                )}

                {/* ── Price Simulator ── */}
                {starsView === 'simulator' && (
                <>
                <div className="sa__sim-banner">
                  <div className="sa__sim-slider-wrap">
                    <div className="sa__sim-lbl">
                      Price change: {priceAdj > 0 ? '+' : ''}{priceAdj} MAD / unit
                      {priceAdj < 0 && (
                        <span style={{ color: '#EF9F27', marginLeft: '0.5rem' }}>
                          — lower price, need more orders to compensate
                        </span>
                      )}
                      {priceAdj > 0 && (
                        <span style={{ color: '#9CA3AF', marginLeft: '0.5rem' }}>
                          — higher price, same profit with fewer orders
                        </span>
                      )}
                    </div>
                    <input
                      type="range"
                      className="sa__slider"
                      min={-50}
                      max={100}
                      step={1}
                      value={priceAdj}
                      onChange={e => { setPriceAdj(+e.target.value); setVolAdj(0); }}
                    />
                  </div>
                  <div className="sa__sim-slider-wrap">
                    <div className="sa__sim-lbl">
                      Volume change: {volAdj > 0 ? '+' : ''}{volAdj}%
                      {priceAdj !== 0 && volAdj === 0 && (
                        <span style={{ color: '#9CA3AF', marginLeft: '0.5rem' }}>
                          — slide to set expected order change
                        </span>
                      )}
                    </div>
                    <input
                      type="range"
                      className="sa__slider"
                      min={-50}
                      max={100}
                      step={1}
                      value={volAdj}
                      onChange={e => setVolAdj(+e.target.value)}
                    />
                  </div>
                  <div className="sa__sim-result">
                    <div className="sa__sim-lbl">Projected Profit (Stars)</div>
                    <div
                      className="sa__sim-result-val"
                      style={{ color: starsProj >= starsBase ? '#1D9E75' : '#E24B4A' }}
                    >
                      {fmtMAD(starsProj)}
                      <span className="sa__sim-result-delta">
                        ({starsProj >= starsBase ? '+' : ''}
                        {fmtMAD(starsProj - starsBase)})
                      </span>
                    </div>
                    <div className="sa__sim-result-base">Base: {fmtMAD(starsBase)}</div>
                  </div>
                </div>

                {stars.length === 0 ? (
                  <EmptyState emoji="★" message="No Stars yet" sub="Criteria: ROI ≥ 25% and ≥ 10 delivered orders" />
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 p-4">
                    {stars.map(p => {
                      const b      = roiBadge(p.roi || 0);
                      const ppp    = p.profitPerProduct || 0;
                      const deliv  = p.deliveredOrders || 0;
                      const newPPP = ppp + priceAdj;
                      const proj   = deliv * newPPP * (1 + volAdj / 100);
                      const changed = priceAdj !== 0 || volAdj !== 0;

                      // Breakeven: how many extra orders needed to match current profit if price drops
                      // extra = deliv * (-priceAdj) / newPPP  (only when newPPP > 0 and price dropped)
                      const breakEvenExtra = (priceAdj < 0 && newPPP > 0)
                        ? Math.ceil((deliv * Math.abs(priceAdj)) / newPPP)
                        : null;

                      // If price rises: how many fewer orders you can afford and still break even
                      const savedOrders = (priceAdj > 0 && newPPP > 0)
                        ? Math.floor(deliv - (deliv * ppp) / newPPP)
                        : null;

                      return (
                        <div key={p._id} className="rounded-xl border border-border bg-card p-4 flex flex-col gap-3 shadow-card hover:shadow-card-md transition-all duration-200">
                          <div className="flex items-start justify-between gap-2">
                            <span
                              onClick={e => handleNameClick(e, p.imgUri)}
                              style={{ cursor: p.imgUri ? 'pointer' : 'default', fontWeight: 600, fontSize: '0.8rem', flex: 1, minWidth: 0 }}
                              title={p.imgUri ? 'Click to see product image' : ''}
                            >
                              {truncate(p.name, 30)}
                            </span>
                            <span className={`sa__roi-badge ${b.cls}`}>{b.label}</span>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {p.url ? <a href={p.url} target="_blank" rel="noopener noreferrer">{p.Sku}</a> : p.Sku} · {p.store?.name}
                          </div>
                          <div className="grid grid-cols-3 gap-1 rounded-lg bg-muted/40 px-2 py-2">
                            <div>
                              <span className="block text-[10px] text-muted-foreground uppercase tracking-wide">Delivered</span>
                              <span className="block text-sm font-bold text-foreground mt-0.5">{deliv}</span>
                            </div>
                            <div>
                              <span className="block text-[10px] text-muted-foreground uppercase tracking-wide">Profit/unit</span>
                              <span className="block text-sm font-bold text-foreground mt-0.5" style={{ color: '#1D9E75' }}>
                                {fmtMAD(ppp)}
                              </span>
                            </div>
                            <div>
                              <span className="block text-[10px] text-muted-foreground uppercase tracking-wide">Total Profit</span>
                              <span className="block text-sm font-bold text-foreground mt-0.5" style={{ color: '#1D9E75' }}>
                                {fmtMAD(p.totalProfit || 0)}
                              </span>
                            </div>
                          </div>
                          {changed && newPPP > 0 && (
                            <div style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '0.5px solid #F0F0F0' }}>
                              <div
                                className="sa__seg-proj"
                                style={{ color: proj >= (p.totalProfit || 0) ? '#1D9E75' : '#E24B4A' }}
                              >
                                Projected: {fmtMAD(proj)}&nbsp;
                                ({proj >= (p.totalProfit || 0) ? '+' : ''}
                                {fmtMAD(proj - (p.totalProfit || 0))})
                              </div>
                              {breakEvenExtra !== null && (
                                <div style={{ fontSize: '0.68rem', color: '#EF9F27', marginTop: '0.2rem' }}>
                                  Need +{breakEvenExtra} more orders to break even at this price
                                </div>
                              )}
                              {savedOrders !== null && savedOrders > 0 && (
                                <div style={{ fontSize: '0.68rem', color: '#1D9E75', marginTop: '0.2rem' }}>
                                  Same profit even with {savedOrders} fewer orders at this price
                                </div>
                              )}
                            </div>
                          )}
                          {changed && newPPP <= 0 && (
                            <div style={{ fontSize: '0.68rem', color: '#E24B4A', marginTop: '0.5rem' }}>
                              Price cut too deep — profit per unit would be negative
                            </div>
                          )}
                          <PriceWidget
                            storeId={(p.store as any)?._id || (p.store as any) || selectedStore}
                            sku={p.Sku}
                            token={contextStore.token}
                            showProfit
                            buyingRate={p.productGroup?.buyingRate || undefined}
                            ourFees={(() => { const t = (p.productGroup?.transportFees||0)+(p.productGroup?.storageFees||0)+(p.productGroup?.packageFees||0); return t > 0 ? t : undefined; })()}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
                </>
                )}
              </>
            )}

            {/* ── Bleeding ──────────────────────────────────────────────── */}
            {activeTab === 'bleeding' && (
              bleeding.length === 0 ? (
                <EmptyState emoji="✅" message="All products profitable" sub="No bleeding SKUs found in this period" />
              ) : (() => {
                const totalBleeding = bleeding.reduce((s, p) => s + Math.min(0, p.totalProfit || 0), 0);
                return (
                  <>
                    <div className="flex flex-wrap items-center gap-3 border-b border-red-200 bg-red-50 px-4 py-3 dark:border-red-900 dark:bg-red-950/20">
                      <div className="flex-1">
                        <p className="text-sm font-bold text-red-700 dark:text-red-400">
                          ⚠ Losing {fmtMAD(Math.abs(totalBleeding))} across {bleeding.length} SKU{bleeding.length > 1 ? 's' : ''} this period
                        </p>
                        <p className="text-xs text-red-600/80 dark:text-red-500">Every order on these products makes it worse — act on the worst ones first</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 p-4">
                      {bleeding.map(p => {
                        const chart     = dailyData[p._id];
                        const isLoading = loadingDaily[p._id];
                        const delivered = p.deliveredOrders || 0;
                        const returned  = p.returnedOrders  || 0;
                        const ppp       = p.profitPerProduct || 0;
                        const total     = p.totalProfit || 0;
                        const loss      = p.loss || 0;

                        const costPerUnit   = (p.buyingRate || 0) + (p.transportFees || 0) + (p.storageFees || 0) + (p.packageFees || 0);
                        const avgPayout     = delivered > 0 ? (p.totalPayout || 0) / delivered : 0;
                        const priceGap      = costPerUnit - avgPayout; // MAD needed to raise per unit to break even
                        const returnRate    = (delivered + returned) > 0 ? (returned / (delivered + returned)) * 100 : 0;

                        const isPricingIssue = ppp < 0;
                        const isReturnIssue  = ppp >= 0 && total < 0;
                        const isBothIssues   = ppp < 0 && loss > 0 && returned > 0;

                        // Root cause label
                        let causeLabel = '';
                        let causeColor = '#E24B4A';
                        if (isBothIssues)      { causeLabel = 'Pricing + Returns'; }
                        else if (isPricingIssue) { causeLabel = 'Pricing / Costs'; }
                        else if (isReturnIssue)  { causeLabel = 'High Returns'; causeColor = '#EF9F27'; }

                        // Recommendations
                        const recs: string[] = [];
                        if (isPricingIssue) {
                          recs.push(`You lose ${fmtMAD(Math.abs(ppp))} on every single delivery — stop promoting this SKU immediately`);
                          recs.push(`Your average Jumia payout is ${fmtMAD(avgPayout)} but your costs are ${fmtMAD(costPerUnit)} — you need at least ${fmtMAD(priceGap)} more per unit`);
                          recs.push(`Raise your selling price so Jumia pays at least ${fmtMAD(costPerUnit)} per unit after their commission`);
                          recs.push('Check if the product group costs (buying rate, transport, storage, package) are correct — a data entry error could be causing a false loss');
                          if (delivered > 3) recs.push('Pause any ads or promotions on this SKU — you are paying to lose money faster');
                        }
                        if (isReturnIssue || (isBothIssues && returned > 0)) {
                          recs.push(`${returnRate.toFixed(0)}% return rate — you lost ${fmtMAD(loss)} just from returns eating your buying cost`);
                          recs.push('Investigate why customers return: wrong description, photos that do not match the real product, or quality issue');
                          recs.push('Check your product listing — if dimensions, color, or specs are inaccurate, fix them now to stop returns');
                          recs.push('A high return rate also hurts your Jumia seller score — fixing this protects your other SKUs too');
                        }
                        if (!isPricingIssue && !isReturnIssue && !isBothIssues) {
                          recs.push(`Total loss of ${fmtMAD(Math.abs(total))} — review this SKU carefully`);
                          recs.push('Verify the product group costs are correctly set for this product');
                        }

                        return (
                          <div key={p._id} className="rounded-xl border border-red-200 bg-card p-4 flex flex-col gap-3 shadow-card border-l-4 border-l-red-500 hover:shadow-card-md transition-all duration-200 dark:border-red-900 dark:border-l-red-500">
                            <div className="flex items-start justify-between gap-2">
                              <span
                                onClick={e => handleNameClick(e, p.imgUri)}
                                style={{ cursor: p.imgUri ? 'pointer' : 'default', fontWeight: 600, fontSize: '0.8rem', flex: 1, minWidth: 0 }}
                                title={p.imgUri ? 'Click to see product image' : ''}
                              >
                                {truncate(p.name, 30)}
                              </span>
                              <span className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ background: causeColor + '18', color: causeColor, border: `1px solid ${causeColor}40` }}>
                                {causeLabel}
                              </span>
                            </div>
                            <div className="text-xs text-muted-foreground">{p.url ? <a href={p.url} target="_blank" rel="noopener noreferrer">{p.Sku}</a> : p.Sku} · {p.store?.name}</div>

                            <div className="grid grid-cols-3 gap-1 rounded-lg bg-muted/40 px-2 py-2">
                              <div>
                                <span className="block text-[10px] text-muted-foreground uppercase tracking-wide">Loss total</span>
                                <span className="block text-sm font-bold text-foreground mt-0.5" style={{ color: '#E24B4A' }}>{fmtMAD(Math.abs(total))}</span>
                              </div>
                              <div>
                                <span className="block text-[10px] text-muted-foreground uppercase tracking-wide">Loss/unit</span>
                                <span className="block text-sm font-bold text-foreground mt-0.5" style={{ color: '#E24B4A' }}>{fmtMAD(Math.abs(ppp))}</span>
                              </div>
                              <div>
                                <span className="block text-[10px] text-muted-foreground uppercase tracking-wide">Delivered</span>
                                <span className="block text-sm font-bold text-foreground mt-0.5">{delivered}</span>
                              </div>
                              <div>
                                <span className="block text-[10px] text-muted-foreground uppercase tracking-wide">Returned</span>
                                <span className="block text-sm font-bold text-foreground mt-0.5" style={{ color: returned > 0 ? '#EF9F27' : undefined }}>{returned}</span>
                              </div>
                              {isPricingIssue && (
                                <div>
                                  <span className="block text-[10px] text-muted-foreground uppercase tracking-wide">Price gap</span>
                                  <span className="block text-sm font-bold text-foreground mt-0.5" style={{ color: '#E24B4A' }}>+{fmtMAD(priceGap)} needed</span>
                                </div>
                              )}
                              {returnRate > 0 && (
                                <div>
                                  <span className="block text-[10px] text-muted-foreground uppercase tracking-wide">Return rate</span>
                                  <span className="block text-sm font-bold text-foreground mt-0.5" style={{ color: '#EF9F27' }}>{returnRate.toFixed(0)}%</span>
                                </div>
                              )}
                            </div>

                            {/* Cost breakdown */}
                            <div className="flex flex-wrap items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-xs dark:bg-red-950/20">
                              <span>Avg payout: <strong>{fmtMAD(avgPayout)}</strong></span>
                              <span style={{ color: '#9CA3AF' }}>vs</span>
                              <span>Costs: <strong>{fmtMAD(costPerUnit)}</strong></span>
                              <span style={{ fontSize: '0.7rem', color: '#9CA3AF' }}>
                                (buy {fmtMAD(p.buyingRate||0)} + ship {fmtMAD(p.transportFees||0)} + store {fmtMAD(p.storageFees||0)} + pack {fmtMAD(p.packageFees||0)})
                              </span>
                            </div>

                            {/* Daily chart */}
                            {chart ? (
                              <div style={{ marginBottom: '0.5rem' }}>
                                <ResponsiveContainer width="100%" height={70}>
                                  <LineChart
                                    data={chart.map(d => ({ ...d, activeOrders: activeOrders(d) }))}
                                    margin={{ top: 2, right: 4, left: -30, bottom: 0 }}
                                  >
                                    <XAxis dataKey="date" hide />
                                    <YAxis hide />
                                    <Tooltip
                                      content={({ active, payload, label }: any) =>
                                        active && payload?.length ? (
                                          <div className="sa__tooltip">
                                            <div style={{ color: '#9CA3AF', fontSize: '0.65rem' }}>{label}</div>
                                            {payload.map((entry: any) => (
                                              <div key={entry.dataKey} style={{ color: entry.color }}>{entry.name}: {entry.value}</div>
                                            ))}
                                          </div>
                                        ) : null
                                      }
                                    />
                                    <Line type="monotone" dataKey="activeOrders" name="In pipeline" stroke="#9CA3AF" strokeWidth={1} strokeDasharray="3 3" dot={false} />
                                    <Line
                                      type="monotone"
                                      dataKey="deliveredOrders"
                                      name="Delivered"
                                      stroke="#E24B4A"
                                      strokeWidth={1.5}
                                      dot={(props: any) => {
                                        const { cx, cy, payload } = props;
                                        return <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={activeOrders(payload) > 0 ? 2 : 3} fill="#E24B4A" stroke="none" />;
                                      }}
                                    />
                                  </LineChart>
                                </ResponsiveContainer>
                                <div style={{ fontSize: '0.62rem', color: '#9CA3AF' }}>
                                  By creation date — each delivered order on this chart is a loss
                                </div>
                              </div>
                            ) : isLoading ? (
                              <div style={{ fontSize: '0.75rem', color: '#9CA3AF', margin: '0.5rem 0' }}>Loading chart…</div>
                            ) : null}

                            <div className="rounded-lg bg-muted/50 p-3 space-y-1">
                              <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1.5">What to do</div>
                              {recs.map((r, i) => <div key={i} className="text-xs text-muted-foreground leading-relaxed">· {r}</div>)}
                            </div>

                            <PriceWidget
                              storeId={(p.store as any)?._id || (p.store as any) || selectedStore}
                              sku={p.Sku}
                              token={contextStore.token}
                              showProfit
                              buyingRate={p.productGroup?.buyingRate || undefined}
                              ourFees={(() => { const t = (p.productGroup?.transportFees||0)+(p.productGroup?.storageFees||0)+(p.productGroup?.packageFees||0); return t > 0 ? t : undefined; })()}
                            />

                            {/* Track button */}
                            {isTracked(p._id) ? (
                              <div className="sa__track-active">
                                <span className="sa__track-active-label">📌 Watching</span>
                                <span className="sa__track-reason-pill">
                                  {trackedSkus.find(t => t.productId === p._id)?.reason}
                                </span>
                                <button className="sa__track-remove" onClick={() => removeFromTracking(p._id)}>✕ Remove</button>
                              </div>
                            ) : addTrackingFor === p._id ? (
                              <div className="sa__track-form">
                                <div className="sa__track-form-presets">
                                  {TRACKING_PRESETS.map(preset => (
                                    <button
                                      key={preset}
                                      className={`sa__track-preset ${trackingReason === preset ? 'sa__track-preset--active' : ''}`}
                                      onClick={() => setTrackingReason(preset)}
                                    >
                                      {preset}
                                    </button>
                                  ))}
                                </div>
                                <input
                                  className="sa__track-input"
                                  placeholder="Or type a custom reason…"
                                  value={trackingReason}
                                  onChange={e => setTrackingReason(e.target.value)}
                                  onKeyDown={e => e.key === 'Enter' && addToTracking(p._id, trackingReason)}
                                />
                                <div className="sa__track-form-actions">
                                  <button className="sa__track-confirm" onClick={() => addToTracking(p._id, trackingReason)}>
                                    Add to Watching
                                  </button>
                                  <button className="sa__track-cancel" onClick={() => { setAddTrackingFor(null); setTrackingReason(''); }}>
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <button className="sa__track-btn" onClick={() => { setAddTrackingFor(p._id); setTrackingReason(''); }}>
                                📌 Track this SKU
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                );
              })()
            )}

            {/* ── Hidden Gems ───────────────────────────────────────────── */}
            {activeTab === 'gems' && (
              gems.length === 0 ? (
                <EmptyState emoji="◆" message="No Hidden Gems" sub="Criteria: ROI ≥ 50% and fewer than 15 delivered orders" />
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 p-4">
                  {gems.map(p => {
                    const b         = roiBadge(p.roi || 0);
                    const chart     = dailyData[p._id];
                    const isLoading = loadingDaily[p._id];
                    const trend     = chart ? getRecentTrend(chart) : 'flat';
                    const trendColor = trend === 'up' ? '#1D9E75' : trend === 'down' ? '#E24B4A' : '#9CA3AF';
                    const trendLabel = trend === 'up' ? '↑ Growing' : trend === 'down' ? '↓ Declining' : '→ Flat';
                    return (
                      <div key={p._id} className="rounded-xl border border-border bg-card p-4 flex flex-col gap-3 shadow-card hover:shadow-card-md transition-all duration-200">
                        <div className="flex items-start justify-between gap-2">
                          <span
                            onClick={e => handleNameClick(e, p.imgUri)}
                            style={{ cursor: p.imgUri ? 'pointer' : 'default', fontWeight: 600, fontSize: '0.8rem', flex: 1, minWidth: 0 }}
                            title={p.imgUri ? 'Click to see product image' : ''}
                          >
                            {truncate(p.name, 30)}
                          </span>
                          <span className={`sa__roi-badge ${b.cls}`}>{b.label}</span>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {p.url ? <a href={p.url} target="_blank" rel="noopener noreferrer">{p.Sku}</a> : p.Sku} · {p.store?.name}
                          {chart && (
                            <span style={{ marginLeft: '0.5rem', fontSize: '0.7rem', color: trendColor, fontWeight: 600 }}>
                              {trendLabel}
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-3 gap-1 rounded-lg bg-muted/40 px-2 py-2">
                          <div>
                            <span className="block text-[10px] text-muted-foreground uppercase tracking-wide">Delivered</span>
                            <span className="block text-sm font-bold text-foreground mt-0.5">{p.deliveredOrders}</span>
                          </div>
                          <div>
                            <span className="block text-[10px] text-muted-foreground uppercase tracking-wide">Profit/unit</span>
                            <span className="block text-sm font-bold text-foreground mt-0.5" style={{ color: '#1D9E75' }}>
                              {fmtMAD(p.profitPerProduct || 0)}
                            </span>
                          </div>
                          <div>
                            <span className="block text-[10px] text-muted-foreground uppercase tracking-wide">ROI</span>
                            <span className="block text-sm font-bold text-foreground mt-0.5" style={{ color: '#1D9E75' }}>
                              {fmtPct(p.roi || 0)}
                            </span>
                          </div>
                        </div>

                        {/* Daily chart */}
                        {chart ? (
                          <div style={{ marginBottom: '0.5rem' }}>
                            <ResponsiveContainer width="100%" height={70}>
                              <LineChart
                                data={chart.map(d => ({ ...d, activeOrders: activeOrders(d) }))}
                                margin={{ top: 2, right: 4, left: -30, bottom: 0 }}
                              >
                                <XAxis dataKey="date" hide />
                                <YAxis hide />
                                <Tooltip
                                  content={({ active, payload, label }: any) =>
                                    active && payload?.length ? (
                                      <div className="sa__tooltip">
                                        <div style={{ color: '#9CA3AF', fontSize: '0.65rem' }}>{label}</div>
                                        {payload.map((entry: any) => (
                                          <div key={entry.dataKey} style={{ color: entry.color }}>
                                            {entry.name}: {entry.value}
                                          </div>
                                        ))}
                                      </div>
                                    ) : null
                                  }
                                />
                                <Line
                                  type="monotone"
                                  dataKey="activeOrders"
                                  name="In pipeline"
                                  stroke="#9CA3AF"
                                  strokeWidth={1}
                                  strokeDasharray="3 3"
                                  dot={false}
                                />
                                <Line
                                  type="monotone"
                                  dataKey="deliveredOrders"
                                  name="Delivered"
                                  stroke="#1D9E75"
                                  strokeWidth={1.5}
                                  dot={(props: any) => {
                                    const { cx, cy, payload } = props;
                                    return activeOrders(payload) > 0
                                      ? <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={2} fill="#1D9E75" stroke="none" />
                                      : <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={3} fill="#E24B4A" stroke="none" />;
                                  }}
                                />
                              </LineChart>
                            </ResponsiveContainer>
                            <div style={{ fontSize: '0.62rem', color: '#9CA3AF' }}>
                              By creation date · Solid = delivered · Dashed = in pipeline · Red = no orders
                            </div>
                          </div>
                        ) : isLoading ? (
                          <div style={{ fontSize: '0.75rem', color: '#9CA3AF', margin: '0.5rem 0' }}>Loading chart…</div>
                        ) : null}

                        <PriceWidget
                          storeId={(p.store as any)?._id || (p.store as any) || selectedStore}
                          sku={p.Sku}
                          token={contextStore.token}
                          showProfit
                          buyingRate={p.productGroup?.buyingRate || undefined}
                          ourFees={(() => { const t = (p.productGroup?.transportFees||0)+(p.productGroup?.storageFees||0)+(p.productGroup?.packageFees||0); return t > 0 ? t : undefined; })()}
                        />

                        <div className="flex gap-2 mt-1">
                          <button
                            className="flex-1 rounded-lg border border-blue-300 bg-blue-50 px-2 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-100 transition-colors dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-400"
                            title="Create a new listing for this product to increase visibility"
                          >
                            Add New Listing
                          </button>
                          <button
                            className="flex-1 rounded-lg border border-emerald-300 bg-emerald-50 px-2 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100 transition-colors dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-400"
                            title="Lower price to attract volume — high ROI means you have room to reduce and still profit"
                          >
                            Try Lower Price
                          </button>
                        </div>

                        {/* Track button */}
                        {isTracked(p._id) ? (
                          <div className="sa__track-active">
                            <span className="sa__track-active-label">📌 Watching</span>
                            <span className="sa__track-reason-pill">
                              {trackedSkus.find(t => t.productId === p._id)?.reason}
                            </span>
                            <button className="sa__track-remove" onClick={() => removeFromTracking(p._id)}>✕ Remove</button>
                          </div>
                        ) : addTrackingFor === p._id ? (
                          <div className="sa__track-form">
                            <div className="sa__track-form-presets">
                              {TRACKING_PRESETS.map(preset => (
                                <button
                                  key={preset}
                                  className={`sa__track-preset ${trackingReason === preset ? 'sa__track-preset--active' : ''}`}
                                  onClick={() => setTrackingReason(preset)}
                                >
                                  {preset}
                                </button>
                              ))}
                            </div>
                            <input
                              className="sa__track-input"
                              placeholder="Or type a custom reason…"
                              value={trackingReason}
                              onChange={e => setTrackingReason(e.target.value)}
                              onKeyDown={e => e.key === 'Enter' && addToTracking(p._id, trackingReason)}
                            />
                            <div className="sa__track-form-actions">
                              <button className="sa__track-confirm" onClick={() => addToTracking(p._id, trackingReason)}>
                                Add to Watching
                              </button>
                              <button className="sa__track-cancel" onClick={() => { setAddTrackingFor(null); setTrackingReason(''); }}>
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button className="sa__track-btn" onClick={() => { setAddTrackingFor(p._id); setTrackingReason(''); }}>
                            📌 Track this SKU
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )
            )}

            {/* ── Rising Stars ──────────────────────────────────────────── */}
            {activeTab === 'rising' && (
              rising.length === 0 ? (
                <EmptyState emoji="↑" message="No Rising Stars yet" sub="Criteria: profit/unit > 0, at least 1 delivered order, not yet a Star" />
              ) : (
                <>
                  <SectionBanner stats={[
                    { label: 'climbing to Star', value: `↑ ${rising.length} products`, color: 'green' },
                    { label: 'sorted by recent activity + proximity to threshold', value: 'ROI ≥ 25% + 10 orders', color: 'default' },
                  ]} />
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 p-4">
                    {rising.map(p => {
                      const chart      = dailyData[p._id];
                      const isLoading  = loadingDaily[p._id];
                      const trend      = chart ? getRecentTrend(chart) : 'flat';
                      const { level: urgLevel, zeroTail } = getUrgency(chart);
                      const ucfg       = urgencyConfig[urgLevel];
                      const trendColor = trend === 'up' ? '#1D9E75' : trend === 'down' ? '#E24B4A' : '#9CA3AF';
                      const trendLabel = trend === 'up' ? '↑ Growing' : trend === 'down' ? '↓ Declining' : '';

                      const roi          = p.roi || 0;
                      const delivered    = p.deliveredOrders || 0;
                      const needsOrders  = roi >= 25 && delivered < 10;
                      const needsRoi     = delivered >= 10 && roi < 25;
                      const needsBoth    = roi < 25 && delivered < 10;
                      const ordersLeft   = Math.max(0, 10 - delivered);
                      const roiLeft      = Math.max(0, 25 - roi);

                      // Gap badge
                      let gapLabel = '';
                      let gapColor = '#378ADD';
                      if (needsOrders) {
                        gapLabel = `${ordersLeft} more order${ordersLeft > 1 ? 's' : ''} to ★`;
                        gapColor = ordersLeft <= 2 ? '#1D9E75' : '#378ADD';
                      } else if (needsRoi) {
                        gapLabel = `ROI needs +${roiLeft.toFixed(0)}% to ★`;
                        gapColor = '#EF9F27';
                      } else if (needsBoth) {
                        gapLabel = `${ordersLeft} orders + ROI +${roiLeft.toFixed(0)}% to ★`;
                        gapColor = '#9CA3AF';
                      }

                      // Recommendations
                      const recs: string[] = [];
                      if (needsOrders) {
                        recs.push(`Only ${ordersLeft} more delivered order${ordersLeft > 1 ? 's' : ''} needed to reach Star status — push now`);
                        recs.push('Check your price vs competitors — even 5 MAD lower can trigger the next wave of orders');
                        recs.push('Activate Jumia Express if not active — it gives a ranking boost and badge that increases click rate');
                        if (roi > 40) recs.push(`ROI is ${roi.toFixed(0)}% — you have room to cut price temporarily to hit 10 deliveries fast`);
                      } else if (needsRoi) {
                        recs.push(`Volume is there (${delivered} orders) but ROI is ${roi.toFixed(0)}% — raise price by a few MAD to push past 25%`);
                        recs.push('Test a small price increase — if orders hold, your ROI improves and you unlock Star status');
                        recs.push('Review your product group costs — if buying/transport fees are misconfigured, ROI may be understated');
                      } else {
                        recs.push('Still early — focus on getting consistent daily orders before optimizing price');
                        recs.push('Search this product on Jumia and compare your listing with top sellers — match their keywords and images');
                        recs.push('Ask a friend or family to search and buy once — it seeds the Jumia algorithm and helps ranking');
                      }
                      if (urgLevel !== 'none') {
                        recs.unshift(`${zeroTail} day${zeroTail > 1 ? 's' : ''} with no new orders — investigate immediately before momentum is lost`);
                      }

                      return (
                        <div
                          key={p._id}
                          className="rounded-xl border border-border bg-card p-4 flex flex-col gap-3 shadow-card hover:shadow-card-md transition-all duration-200"
                          style={urgLevel !== 'none' ? { borderColor: ucfg.border, background: ucfg.bg } : {}}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <span
                              onClick={e => handleNameClick(e, p.imgUri)}
                              style={{ cursor: p.imgUri ? 'pointer' : 'default', fontWeight: 600, fontSize: '0.8rem', flex: 1, minWidth: 0 }}
                              title={p.imgUri ? 'Click to see product image' : ''}
                            >
                              {truncate(p.name, 30)}
                            </span>
                            <span
                              className="sa__roi-badge"
                              style={{ background: gapColor + '20', color: gapColor, border: `1px solid ${gapColor}40`, borderRadius: '4px', padding: '0.1rem 0.4rem', fontSize: '0.7rem', fontWeight: 600 }}
                            >
                              {gapLabel}
                            </span>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {p.url ? <a href={p.url} target="_blank" rel="noopener noreferrer">{p.Sku}</a> : p.Sku} · {p.store?.name}
                            {trendLabel && (
                              <span style={{ marginLeft: '0.5rem', fontSize: '0.7rem', color: trendColor, fontWeight: 600 }}>
                                {trendLabel}
                              </span>
                            )}
                          </div>

                          <div className="grid grid-cols-3 gap-1 rounded-lg bg-muted/40 px-2 py-2">
                            <div>
                              <span className="block text-[10px] text-muted-foreground uppercase tracking-wide">Delivered</span>
                              <span className="block text-sm font-bold text-foreground mt-0.5">{delivered}</span>
                            </div>
                            <div>
                              <span className="block text-[10px] text-muted-foreground uppercase tracking-wide">Profit/unit</span>
                              <span className="block text-sm font-bold text-foreground mt-0.5" style={{ color: '#1D9E75' }}>
                                {fmtMAD(p.profitPerProduct || 0)}
                              </span>
                            </div>
                            <div>
                              <span className="block text-[10px] text-muted-foreground uppercase tracking-wide">ROI</span>
                              <span className="block text-sm font-bold text-foreground mt-0.5" style={{ color: roi >= 25 ? '#1D9E75' : '#EF9F27' }}>
                                {fmtPct(roi)}
                              </span>
                            </div>
                          </div>

                          {/* Daily chart */}
                          {chart ? (
                            <div style={{ marginBottom: '0.5rem' }}>
                              <ResponsiveContainer width="100%" height={70}>
                                <LineChart
                                  data={chart.map(d => ({ ...d, activeOrders: activeOrders(d) }))}
                                  margin={{ top: 2, right: 4, left: -30, bottom: 0 }}
                                >
                                  <XAxis dataKey="date" hide />
                                  <YAxis hide />
                                  <Tooltip
                                    content={({ active, payload, label }: any) =>
                                      active && payload?.length ? (
                                        <div className="sa__tooltip">
                                          <div style={{ color: '#9CA3AF', fontSize: '0.65rem' }}>{label}</div>
                                          {payload.map((entry: any) => (
                                            <div key={entry.dataKey} style={{ color: entry.color }}>
                                              {entry.name}: {entry.value}
                                            </div>
                                          ))}
                                        </div>
                                      ) : null
                                    }
                                  />
                                  <Line type="monotone" dataKey="activeOrders" name="In pipeline" stroke="#9CA3AF" strokeWidth={1} strokeDasharray="3 3" dot={false} />
                                  <Line
                                    type="monotone"
                                    dataKey="deliveredOrders"
                                    name="Delivered"
                                    stroke="#378ADD"
                                    strokeWidth={1.5}
                                    dot={(props: any) => {
                                      const { cx, cy, payload } = props;
                                      return activeOrders(payload) > 0
                                        ? <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={2} fill="#378ADD" stroke="none" />
                                        : <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={3} fill="#E24B4A" stroke="none" />;
                                    }}
                                  />
                                </LineChart>
                              </ResponsiveContainer>
                              <div style={{ fontSize: '0.62rem', color: '#9CA3AF' }}>
                                By creation date · Solid = delivered · Dashed = in pipeline · Red = no orders
                              </div>
                            </div>
                          ) : isLoading ? (
                            <div style={{ fontSize: '0.75rem', color: '#9CA3AF', margin: '0.5rem 0' }}>Loading chart…</div>
                          ) : null}

                          {/* Recommendations */}
                          {recs.length > 0 && (
                            <div className="rounded-lg bg-muted/50 p-3 space-y-1">
                              <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1.5">What to do</div>
                              {recs.map((r, i) => (
                                <div key={i} className="text-xs text-muted-foreground leading-relaxed">· {r}</div>
                              ))}
                            </div>
                          )}

                          <PriceWidget
                            storeId={(p.store as any)?._id || (p.store as any) || selectedStore}
                            sku={p.Sku}
                            token={contextStore.token}
                            showProfit
                            buyingRate={p.productGroup?.buyingRate || undefined}
                            ourFees={(() => { const t = (p.productGroup?.transportFees||0)+(p.productGroup?.storageFees||0)+(p.productGroup?.packageFees||0); return t > 0 ? t : undefined; })()}
                          />

                          {/* Track button */}
                          {isTracked(p._id) ? (
                            <div className="sa__track-active">
                              <span className="sa__track-active-label">📌 Watching</span>
                              <span className="sa__track-reason-pill">
                                {trackedSkus.find(t => t.productId === p._id)?.reason}
                              </span>
                              <button className="sa__track-remove" onClick={() => removeFromTracking(p._id)}>✕ Remove</button>
                            </div>
                          ) : addTrackingFor === p._id ? (
                            <div className="sa__track-form">
                              <div className="sa__track-form-presets">
                                {TRACKING_PRESETS.map(preset => (
                                  <button
                                    key={preset}
                                    className={`sa__track-preset ${trackingReason === preset ? 'sa__track-preset--active' : ''}`}
                                    onClick={() => setTrackingReason(preset)}
                                  >
                                    {preset}
                                  </button>
                                ))}
                              </div>
                              <input
                                className="sa__track-input"
                                placeholder="Or type a custom reason…"
                                value={trackingReason}
                                onChange={e => setTrackingReason(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && addToTracking(p._id, trackingReason)}
                              />
                              <div className="sa__track-form-actions">
                                <button className="sa__track-confirm" onClick={() => addToTracking(p._id, trackingReason)}>
                                  Add to Watching
                                </button>
                                <button className="sa__track-cancel" onClick={() => { setAddTrackingFor(null); setTrackingReason(''); }}>
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button className="sa__track-btn" onClick={() => { setAddTrackingFor(p._id); setTrackingReason(''); }}>
                              📌 Track this SKU
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )
            )}

            {/* ── Thin Margin ───────────────────────────────────────────── */}
            {activeTab === 'thin' && (
              thin.length === 0 ? (
                <EmptyState emoji="◎" message="No Thin Margin products" sub="Criteria: ≥ 5 delivered orders, profit/unit between 0–60 MAD" />
              ) : (
                <>
                  <SectionBanner stats={[
                    { label: 'extra profit if +10 MAD (same volume)', value: `+${fmtMAD(thinPlus10)}`, color: 'green' },
                    { label: 'extra orders needed if −10 MAD', value: `${thin.reduce((s, p) => { const n = (p.profitPerProduct||0)-10; return n>0 ? s+Math.ceil(((p.deliveredOrders||0)*10)/n):s; }, 0).toLocaleString()} orders`, color: 'amber' },
                    { label: 'products', value: thin.length, color: 'default' },
                    { label: 'delivered total', value: thin.reduce((s, p) => s + (p.deliveredOrders || 0), 0).toLocaleString(), color: 'default' },
                  ]} />

                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 p-4">
                    {thin.map(p => {
                      const b     = roiBadge(p.roi || 0);
                      const ppp   = p.profitPerProduct || 0;
                      const deliv = p.deliveredOrders || 0;

                      // Price rise: +10 MAD, same volume → extra profit (optimistic, assumes no order loss)
                      const rise10profit = deliv * 10;

                      // Price cut: -10 MAD → new profit/unit, how many extra orders to break even
                      const cutNewPPP = ppp - 10;
                      const cut10extra = cutNewPPP > 0
                        ? Math.ceil((deliv * 10) / cutNewPPP)
                        : null;

                      return (
                        <div key={p._id} className="rounded-xl border border-border bg-card p-4 flex flex-col gap-3 shadow-card hover:shadow-card-md transition-all duration-200">
                          <div className="flex items-start justify-between gap-2">
                            <span
                              onClick={e => handleNameClick(e, p.imgUri)}
                              style={{ cursor: p.imgUri ? 'pointer' : 'default', fontWeight: 600, fontSize: '0.8rem', flex: 1, minWidth: 0 }}
                              title={p.imgUri ? 'Click to see product image' : ''}
                            >
                              {truncate(p.name, 30)}
                            </span>
                            <span className={`sa__roi-badge ${b.cls}`}>{b.label}</span>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {p.url ? <a href={p.url} target="_blank" rel="noopener noreferrer">{p.Sku}</a> : p.Sku} · {p.store?.name}
                          </div>
                          <div className="grid grid-cols-3 gap-1 rounded-lg bg-muted/40 px-2 py-2">
                            <div>
                              <span className="block text-[10px] text-muted-foreground uppercase tracking-wide">Delivered</span>
                              <span className="block text-sm font-bold text-foreground mt-0.5">{deliv}</span>
                            </div>
                            <div>
                              <span className="block text-[10px] text-muted-foreground uppercase tracking-wide">Profit/unit</span>
                              <span className="block text-sm font-bold text-foreground mt-0.5" style={{ color: '#EF9F27' }}>
                                {fmtMAD(ppp)}
                              </span>
                            </div>
                            <div>
                              <span className="block text-[10px] text-muted-foreground uppercase tracking-wide">Total Profit</span>
                              <span className="block text-sm font-bold text-foreground mt-0.5">{fmtMAD(p.totalProfit || 0)}</span>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-2.5 dark:bg-emerald-950/20 dark:border-emerald-900">
                              <div className="text-[10px] text-muted-foreground">+10 MAD price</div>
                              <div className="text-sm font-bold text-emerald-600 dark:text-emerald-400">+{fmtMAD(rise10profit)}</div>
                              <div className="text-[10px] text-muted-foreground">if orders hold</div>
                            </div>
                            <div
                              className="rounded-lg border p-2.5"
                              style={{
                                background: cut10extra !== null ? '#FFFBEB' : '#FEF2F2',
                                borderColor: cut10extra !== null ? '#FDE68A' : '#FECACA',
                              }}
                            >
                              <div className="text-[10px] text-muted-foreground">-10 MAD price</div>
                              {cut10extra !== null ? (
                                <>
                                  <div style={{ fontSize: '0.78rem', fontWeight: 500, color: '#EF9F27' }}>
                                    +{cut10extra} orders
                                  </div>
                                  <div style={{ fontSize: '0.6rem', color: '#9CA3AF' }}>needed to break even</div>
                                </>
                              ) : (
                                <div style={{ fontSize: '0.7rem', color: '#E24B4A' }}>margin too thin</div>
                              )}
                            </div>
                          </div>

                          <PriceWidget
                            storeId={(p.store as any)?._id || (p.store as any) || selectedStore}
                            sku={p.Sku}
                            token={contextStore.token}
                            showProfit
                            buyingRate={p.productGroup?.buyingRate || undefined}
                            ourFees={(() => { const t = (p.productGroup?.transportFees||0)+(p.productGroup?.storageFees||0)+(p.productGroup?.packageFees||0); return t > 0 ? t : undefined; })()}
                          />

                          {/* Track button */}
                          {isTracked(p._id) ? (
                            <div className="sa__track-active">
                              <span className="sa__track-active-label">📌 Watching</span>
                              <span className="sa__track-reason-pill">
                                {trackedSkus.find(t => t.productId === p._id)?.reason}
                              </span>
                              <button className="sa__track-remove" onClick={() => removeFromTracking(p._id)}>✕ Remove</button>
                            </div>
                          ) : addTrackingFor === p._id ? (
                            <div className="sa__track-form">
                              <div className="sa__track-form-presets">
                                {TRACKING_PRESETS.map(preset => (
                                  <button
                                    key={preset}
                                    className={`sa__track-preset ${trackingReason === preset ? 'sa__track-preset--active' : ''}`}
                                    onClick={() => setTrackingReason(preset)}
                                  >
                                    {preset}
                                  </button>
                                ))}
                              </div>
                              <input
                                className="sa__track-input"
                                placeholder="Or type a custom reason…"
                                value={trackingReason}
                                onChange={e => setTrackingReason(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && addToTracking(p._id, trackingReason)}
                              />
                              <div className="sa__track-form-actions">
                                <button className="sa__track-confirm" onClick={() => addToTracking(p._id, trackingReason)}>
                                  Add to Watching
                                </button>
                                <button className="sa__track-cancel" onClick={() => { setAddTrackingFor(null); setTrackingReason(''); }}>
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button className="sa__track-btn" onClick={() => { setAddTrackingFor(p._id); setTrackingReason(''); }}>
                              📌 Track this SKU
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )
            )}

            {/* ── Watched SKUs ───────────────────────────────────────────── */}
            {activeTab === 'watched' && (
              trackedSkus.length === 0 ? (
                <EmptyState
                  emoji="📌"
                  message="No SKUs being tracked yet"
                  sub="Go to any tab (Stars, Gems, Rising…) and click Track this SKU on a card. Add a reason so you remember why you're watching it."
                />
              ) : (
                <>
                  <SectionBanner stats={[
                    { label: `SKU${trackedSkus.length !== 1 ? 's' : ''} tracked`, value: trackedSkus.length, color: 'violet' },
                    { label: 'combined profit this period', value: watchedProducts.length > 0 ? fmtMAD(watchedProducts.reduce((s, { product: p }) => s + (p.totalProfit || 0), 0)) : '—', color: 'green' },
                    { label: 'delivered orders', value: watchedProducts.reduce((s, { product: p }) => s + (p.deliveredOrders || 0), 0), color: 'blue' },
                  ]} />

                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 p-4">
                    {trackedSkus.map(tracking => {
                      const p = products.find(x => x._id === tracking.productId);
                      const chart     = p ? dailyData[p._id] : undefined;
                      const isLoading = p ? loadingDaily[p._id] : false;
                      const trend     = chart ? getRecentTrend(chart) : 'flat';
                      const trendColor = trend === 'up' ? '#1D9E75' : trend === 'down' ? '#E24B4A' : '#9CA3AF';
                      const trendLabel = trend === 'up' ? '↑ Growing' : trend === 'down' ? '↓ Declining' : '→ Flat';
                      const trackedDate = new Date(tracking.trackedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
                      const isEditingThisReason = editingReasonFor === tracking.productId;

                      if (!p) {
                        return (
                          <div key={tracking.productId} className="rounded-xl border border-border bg-card/50 p-4 flex flex-col gap-3 opacity-60 border-t-4 border-t-muted-foreground/30">
                            <div className="flex items-start justify-between gap-2" style={{ color: '#9CA3AF' }}>SKU not found in current period</div>
                            <div className="text-xs text-muted-foreground">{tracking.productId}</div>
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="sa__track-reason-pill">{tracking.reason}</span>
                              <span style={{ fontSize: '0.65rem', color: '#9CA3AF' }}>Since {trackedDate}</span>
                            </div>
                            <div style={{ fontSize: '0.72rem', color: '#9CA3AF', marginTop: '0.5rem' }}>
                              Try widening the date range — this SKU had no orders in the selected period.
                            </div>
                            <button className="sa__track-remove" style={{ marginTop: '0.5rem' }} onClick={() => removeFromTracking(tracking.productId)}>
                              ✕ Remove from tracking
                            </button>
                          </div>
                        );
                      }

                      const b = roiBadge(p.roi || 0);
                      return (
                        <div key={tracking.productId} className="rounded-xl border border-border bg-card p-4 flex flex-col gap-3 shadow-card border-t-4 border-t-violet-500 hover:shadow-card-md transition-all duration-200">
                          <div className="flex items-start justify-between gap-2">
                            <span
                              onClick={e => handleNameClick(e, p.imgUri)}
                              style={{ cursor: p.imgUri ? 'pointer' : 'default', fontWeight: 600, fontSize: '0.8rem', flex: 1, minWidth: 0 }}
                              title={p.imgUri ? 'Click to see product image' : ''}
                            >
                              {truncate(p.name, 30)}
                            </span>
                            <span className={`sa__roi-badge ${b.cls}`}>{b.label}</span>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {p.url ? <a href={p.url} target="_blank" rel="noopener noreferrer">{p.Sku}</a> : p.Sku} · {p.store?.name}
                            {chart && (
                              <span style={{ marginLeft: '0.5rem', fontSize: '0.7rem', color: trendColor, fontWeight: 600 }}>
                                {trendLabel}
                              </span>
                            )}
                          </div>

                          {/* Reason row */}
                          <div className="flex flex-wrap items-center gap-2">
                            {isEditingThisReason ? (
                              <>
                                <div className="sa__track-form-presets" style={{ marginBottom: '0.3rem' }}>
                                  {TRACKING_PRESETS.map(preset => (
                                    <button
                                      key={preset}
                                      className={`sa__track-preset ${editReasonValue === preset ? 'sa__track-preset--active' : ''}`}
                                      onClick={() => setEditReasonValue(preset)}
                                    >
                                      {preset}
                                    </button>
                                  ))}
                                </div>
                                <input
                                  className="sa__track-input"
                                  value={editReasonValue}
                                  onChange={e => setEditReasonValue(e.target.value)}
                                  onKeyDown={e => {
                                    if (e.key === 'Enter') { updateTrackingReason(p._id, editReasonValue); setEditingReasonFor(null); }
                                    if (e.key === 'Escape') setEditingReasonFor(null);
                                  }}
                                  autoFocus
                                />
                                <div className="sa__track-form-actions">
                                  <button className="sa__track-confirm" onClick={() => { updateTrackingReason(p._id, editReasonValue); setEditingReasonFor(null); }}>Save</button>
                                  <button className="sa__track-cancel" onClick={() => setEditingReasonFor(null)}>Cancel</button>
                                </div>
                              </>
                            ) : (
                              <>
                                <span className="sa__track-reason-pill">{tracking.reason}</span>
                                <span style={{ fontSize: '0.65rem', color: '#9CA3AF' }}>Since {trackedDate}</span>
                                <button className="ml-auto text-[10px] text-muted-foreground underline cursor-pointer hover:text-foreground bg-transparent border-none font-inherit" onClick={() => { setEditReasonValue(tracking.reason); setEditingReasonFor(tracking.productId); }}>
                                  edit reason
                                </button>
                              </>
                            )}
                          </div>

                          <div className="grid grid-cols-3 gap-1 rounded-lg bg-muted/40 px-2 py-2">
                            <div>
                              <span className="block text-[10px] text-muted-foreground uppercase tracking-wide">Delivered</span>
                              <span className="block text-sm font-bold text-foreground mt-0.5">{p.deliveredOrders || 0}</span>
                            </div>
                            <div>
                              <span className="block text-[10px] text-muted-foreground uppercase tracking-wide">Profit/unit</span>
                              <span className="block text-sm font-bold text-foreground mt-0.5" style={{ color: (p.profitPerProduct || 0) >= 0 ? '#1D9E75' : '#E24B4A' }}>
                                {fmtMAD(p.profitPerProduct || 0)}
                              </span>
                            </div>
                            <div>
                              <span className="block text-[10px] text-muted-foreground uppercase tracking-wide">ROI</span>
                              <span className="block text-sm font-bold text-foreground mt-0.5" style={{ color: (p.roi || 0) >= 25 ? '#1D9E75' : '#EF9F27' }}>
                                {fmtPct(p.roi || 0)}
                              </span>
                            </div>
                            <div>
                              <span className="block text-[10px] text-muted-foreground uppercase tracking-wide">Total profit</span>
                              <span className="block text-sm font-bold text-foreground mt-0.5">{fmtMAD(p.totalProfit || 0)}</span>
                            </div>
                          </div>

                          {/* Daily chart */}
                          {chart ? (
                            <div style={{ marginBottom: '0.5rem' }}>
                              <ResponsiveContainer width="100%" height={70}>
                                <LineChart
                                  data={chart.map(d => ({ ...d, activeOrders: activeOrders(d) }))}
                                  margin={{ top: 2, right: 4, left: -30, bottom: 0 }}
                                >
                                  <XAxis dataKey="date" hide />
                                  <YAxis hide />
                                  <Tooltip
                                    content={({ active, payload, label }: any) =>
                                      active && payload?.length ? (
                                        <div className="sa__tooltip">
                                          <div style={{ color: '#9CA3AF', fontSize: '0.65rem' }}>{label}</div>
                                          {payload.map((entry: any) => (
                                            <div key={entry.dataKey} style={{ color: entry.color }}>
                                              {entry.name}: {entry.value}
                                            </div>
                                          ))}
                                        </div>
                                      ) : null
                                    }
                                  />
                                  <Line type="monotone" dataKey="activeOrders" name="In pipeline" stroke="#9CA3AF" strokeWidth={1} strokeDasharray="3 3" dot={false} />
                                  <Line
                                    type="monotone"
                                    dataKey="deliveredOrders"
                                    name="Delivered"
                                    stroke="#1D9E75"
                                    strokeWidth={1.5}
                                    dot={(props: any) => {
                                      const { cx, cy, payload } = props;
                                      return activeOrders(payload) > 0
                                        ? <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={2} fill="#1D9E75" stroke="none" />
                                        : <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={3} fill="#E24B4A" stroke="none" />;
                                    }}
                                  />
                                </LineChart>
                              </ResponsiveContainer>
                              <div style={{ fontSize: '0.62rem', color: '#9CA3AF' }}>
                                By creation date · Solid = delivered · Dashed = in pipeline · Red = no orders
                              </div>
                            </div>
                          ) : isLoading ? (
                            <div style={{ fontSize: '0.75rem', color: '#9CA3AF', margin: '0.5rem 0' }}>Loading chart…</div>
                          ) : null}

                          <PriceWidget
                            storeId={(p.store as any)?._id || (p.store as any) || selectedStore}
                            sku={p.Sku}
                            token={contextStore.token}
                            showProfit
                            buyingRate={p.productGroup?.buyingRate || undefined}
                            ourFees={(() => { const t = (p.productGroup?.transportFees||0)+(p.productGroup?.storageFees||0)+(p.productGroup?.packageFees||0); return t > 0 ? t : undefined; })()}
                          />

                          <button className="sa__track-remove" style={{ marginTop: '0.25rem' }} onClick={() => removeFromTracking(p._id)}>
                            ✕ Stop watching this SKU
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </>
              )
            )}
          </div>

        </div>
      )}

      {/* ── Product image popup ──────────────────────────────────────────── */}
      {imgPopup && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 9999 }}
          onClick={() => setImgPopup(null)}
        >
          <div
            style={{
              position: 'absolute',
              top: Math.min(imgPopup.top, window.innerHeight - 240),
              left: Math.min(imgPopup.left, window.innerWidth - 220),
              background: '#fff',
              borderRadius: 14,
              boxShadow: '0 12px 40px rgba(0,0,0,0.22)',
              border: '1px solid #E5E7EB',
              overflow: 'hidden',
              pointerEvents: 'none',
            }}
          >
            <img
              src={imgPopup.uri}
              alt="product"
              style={{ width: 210, height: 210, objectFit: 'contain', display: 'block' }}
              onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SmartAnalyticsPage;
